import type { Dictionary } from '../types'

const dict: Dictionary = {
  common: {
    save: 'Save',
    saving: 'Saving...',
    saved: 'Saved',
    cancel: 'Cancel',
    delete: 'Delete',
    confirm: 'Confirm',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    done: 'Done',
    retry: 'Retry',
    add: 'Add',
    edit: 'Edit',
    update: 'Update',
    loading: 'Loading...',
    search: 'Search',
    filter: 'Filter',
    sort: 'Sort',
    all: 'All',
    yes: 'Yes',
    no: 'No',
    enable: 'Enable',
    disable: 'Disable',
    enabled: 'Enabled',
    disabled: 'Disabled',
    optional: 'Optional',
    unlimited: 'Unlimited',
    unknown: 'Unknown',
    unknownCustomer: 'Unknown customer',
    noData: 'No data yet',
    comingSoon: 'Coming soon',
    export: 'Export',
    signOut: 'Sign out',
    admin: 'Admin',
    owner: 'Owner',
    staff: 'Staff',
    manager: 'Manager',
    agent: 'Agent',
    marketer: 'Marketer',
    finance: 'Finance',
    viewer: 'Viewer',
    viewAll: 'View all',
    today: 'Today',
    more: 'More',
  },

  status: {
    pending: 'Pending',
    confirmed: 'Confirmed',
    paid: 'Paid',
    completed: 'Completed',
    cancelled: 'Cancelled',
    noShow: 'No-show',
    inProgress: 'In progress',
    resolved: 'Resolved',
    closed: 'Closed',
    listed: 'Listed',
    unlisted: 'Unlisted',
    unpaid: 'Unpaid',
  },

  nav: {
    overview: 'Overview',
    conversations: 'Conversations',
    orders: 'Orders',
    bookings: 'Bookings',
    bookingsFnb: 'Reservations',
    customers: 'Customers',
    products: 'Products',
    productsBooking: 'Services',
    productsConsultation: 'Engagements',
    productsSupport: 'Service items',
    catalog: 'Catalog',
    inquiries: 'Inquiries',
    followUps: 'Follow-ups',
    knowledge: 'Knowledge',
    loyalty: 'Loyalty',
    coupons: 'Coupons',
    broadcasts: 'Broadcasts',
    agent: 'AI Agent',
    sandbox: 'Test Chat',
    sandboxMode: 'Test Mode',
    storefront: 'Storefront',
    insights: 'Insights',
    settings: 'Settings',
    usage: 'Usage',
  },

  switcher: {
    needsAttentionPlural: '{count} other brands need attention',
    needsAttentionSingular: '1 other brand needs attention',
    allClear: 'Other brands clear',
    triggerAriaLabel: 'Switch brand',
    listboxAriaLabel: 'Available brands',
    loading: 'Loading brands…',
    switchError: 'Failed to switch brand. Please try again.',
    notAccessibleError: 'You do not have access to that brand',
    networkError: 'Network connection failed',
    unreadBadgeSingular: '{count} unread',
    unreadBadgePlural: '{count} unread',
  },

  overview: {
    title: 'Overview',
    subtitle: 'Your AI assistant handles customer conversations around the clock',
    subtitleBooking: 'AI takes appointments, follows up, and books 24×7',
    subtitleBookingFnb: 'AI takes reservations, follows up, and seats guests 24×7',
    subtitleConsultation: 'AI captures inquiries, qualifies leads, and routes 24×7',
    subtitleSupport: 'AI answers FAQs, opens tickets, and deflects routine asks 24×7',
    conversations: 'Conversations',
    orders: 'Orders',
    bookings: 'Bookings',
    bookingsFnb: 'Reservations',
    inquiriesNew: 'New inquiries',
    inquiriesPending: 'Open inquiries',
    revenue: 'Revenue',
    monthlyUsage: 'AI credits this month',
    percentUsed: '% used',
    activeConversations: 'Active conversations: {count}',
    viewAllConversations: 'View all conversations →',
    topCustomers: 'Top Customers',
    viewAll: 'View all',
    aiValueOrders: 'Orders via AI',
    aiValueInquiries: 'Inquiries handled by AI',
    aiValueInquiriesSub: 'No human handoff needed',
  },

  products: {
    title: 'Products',
    addProduct: 'Add product / service',
    searchPlaceholder: 'Search products...',
    noProducts: 'No products yet',
    noProductsHint: 'Add a product or service to get started',
    seedSamples: {
      button: 'Try sample products',
      loading: 'Seeding…',
      hint: 'Loads a small starter catalogue you can tweak or remove later.',
      failed: 'Failed to seed samples',
    },
    product: 'Product',
    service: 'Service',
    inquiry: 'Inquiry',
    package: 'Package',
    packageTypeOption: 'Package (multi-session)',
    packageHint: 'Customer prepays for multiple redeemable sessions of a chosen service.',
    packageTargetService: 'Target service',
    packageTargetServiceHint: 'Each redemption books one appointment for this service.',
    packageSelectTargetService: 'Please select',
    packageTotalSessions: 'Total sessions',
    packageTotalSessionsPlaceholder: 'e.g. 6',
    packageValidityDays: 'Validity (days)',
    packageValidityDaysPlaceholder: 'e.g. 365 (blank = never expires)',
    packageValidityLabel: 'Validity',
    packageValidityForever: 'Never expires',
    packageValidityDaysFormat: '{days} days',
    packageSessionsShort: '{count} sessions',
    packageRowSessions: '× {count} sessions',
    basicInfo: 'Basic info',
    name: 'Name',
    price: 'Price',
    pricePlaceholder: 'e.g. 280',
    description: 'Description',
    descriptionPlaceholder: 'Briefly describe your product or service...',
    salesSettings: 'Sales settings',
    orderingMode: 'Ordering mode',
    directOrder: 'Direct order',
    inquiryOnly: 'Inquiry only',
    fulfillment: 'Fulfillment',
    delivery: 'Delivery',
    pickup: 'Pickup',
    deliveryOrPickup: 'Delivery or pickup',
    na: 'N/A',
    serviceMode: 'Service mode',
    inStore: 'In-store',
    atCustomer: 'At customer',
    online: 'Online',
    hybrid: 'Hybrid',
    paymentTiming: 'Payment timing',
    fullUpfront: 'Full upfront',
    deposit: 'Deposit',
    onCompletion: 'On completion',
    requiresBooking: 'Requires booking',
    requiresBookingYes: 'Customer needs to select a date and time',
    requiresBookingNo: 'Customer can order directly without selecting a time',
    depositPercent: 'Deposit percentage (%)',
    depositPercentPlaceholder: 'e.g. 30',
    priceRange: 'Price range',
    minPrice: 'Min price',
    maxPrice: 'Max price',
    quotationNotes: 'Quotation notes (for AI)',
    quotationNotesPlaceholder: 'e.g. Price depends on area, floor level, and complexity',
    priceRangeHint: 'When a price range is set, the AI will proactively inform customers of the approximate price. Quotation notes help the AI gather relevant details.',
    pickupAddress: 'Pickup address',
    pickupAddressPlaceholder: 'e.g. 123 Nathan Road, Mong Kok',
    pickupNotes: 'Pickup notes',
    pickupNotesPlaceholder: 'e.g. Please show your order number',
    serviceAddress: 'Service address',
    serviceAddressPlaceholder: 'e.g. 1 Gloucester Road, Causeway Bay',
    serviceDuration: 'Service duration (minutes)',
    serviceDurationPlaceholder: 'e.g. 60',
    concurrent: 'Concurrent capacity',
    concurrentDefault: 'Default: 1',
    creating: 'Creating...',
    confirmAdd: 'Confirm',
    draftBanner: 'This {type} is unlisted and not visible to customers. When ready, tap the "Unlisted" button to list it.',
    recentOrders: 'Recent orders',
    variants: 'Variants',
    variantName: 'Variant name',
    variantNamePlaceholder: 'e.g. White / M',
    variantPrice: 'Price (leave blank to use product price)',
    variantPricePlaceholder: 'Price',
    variantStock: 'Stock (leave blank for unlimited)',
    variantStockPlaceholder: 'Stock',
    sku: 'SKU',
    skuPlaceholder: 'e.g. WHT-M-001',
    addVariant: 'Add variant',
    noVariants: 'No variants',
    noVariantsHint: 'No variants set up. Once added, customers will need to choose a variant when ordering.',
    adding: 'Adding...',
    list: 'List',
    unlist: 'Unlist',
    typeLabel: 'Type',
    priceHKD: 'Price (HKD)',
    priceInvalid: 'Price must be 0 or greater.',
    minutes: 'min',
    createdAt: 'Created',
    totalSales: 'Total sales',
    totalRevenue: 'Total revenue',
    orderCount: 'Orders',
    noOrderHistory: 'No order history',
    listNow: 'List now',
    followsProduct: 'follows product',
    stock: 'Stock',
    notFound: 'Product not found',
    serviceDurationLabel: 'Service duration',
    concurrentLabel: 'Concurrent capacity',
    images: 'Product Images',
    uploadImages: 'Upload Images',
    noImages: 'No product images',
    noImagesHint: 'Upload product images — AI will automatically send them to customers when recommending products.',
    setPrimary: 'Set as cover',
    primary: 'Cover',
    uploading: 'Uploading…',
    dragOrClick: 'Drag & drop or click to upload',
    imageFormats: 'Supports JPG, PNG, WebP',
    resourceRequirements: {
      label: 'Resource requirements',
      hint: 'When set, the system auto-allocates free resources at booking time and hides slots that cannot be satisfied.',
      itemLabel: '{quantity}× {label}',
      emptyHint: "No requirements set — bookings won't reserve rooms or equipment.",
      noCategories: 'Create at least one resource category before adding requirements.',
      manageCategories: 'Manage categories',
      addRequirement: 'Add requirement',
      modeCategory: 'By category',
      modeSpecific: 'Specific resources',
      modeHintCategory: 'Any available resource in this category will do.',
      modeHintSpecific: 'Pick exact resources that can fulfil this (e.g. "Machine A or B").',
      selectCategory: 'Select category',
      selectResources: 'Select resources',
      quantity: 'Quantity',
      specificSummary: '{count} specific resource(s)',
      save: 'Save',
      cancel: 'Cancel',
      errAdd: 'Failed to add',
      errRemove: 'Failed to remove',
      errTargetRequired: 'Pick a category, specific resources, or both.',
    },
    pasteExtract: {
      buttonLabel: 'Paste from text',
      buttonTooltip: 'Paste your menu or service list and let AI extract rows',
      title: 'Paste & extract',
      descriptionPaste:
        'Paste your menu, service list, or price sheet. The AI will turn it into rows you can review before saving.',
      descriptionReview: 'Review {count} extracted items. Edit or delete anything that looks off.',
      descriptionDone: 'Done.',
      placeholders: {
        salon:
          'Paste anything — service list, price sheet, menu. E.g.\n\nGel nails — $280 / 90 min\nManicure — $120 / 45 min\nNail art add-on — $50',
        restaurant:
          'Paste anything — menu, price list. E.g.\n\nSignature fried rice — $68\nWonton noodles — $48\nIced lemon tea — $28',
        cafe:
          'Paste anything — drinks menu, pastry list. E.g.\n\nLatte — $38\nIced drip coffee — $32\nCroissant — $28\nCheesecake slice — $45',
        retail:
          'Paste anything — product list, price sheet. E.g.\n\nCotton tee (S/M/L) — $180\nDenim jacket — $680\nCanvas tote — $120',
        medical:
          'Paste anything — service list, consultation fees. E.g.\n\nInitial consultation — $600 / 45 min\nFollow-up — $400 / 30 min\nAcupuncture — $500',
        fitness:
          'Paste anything — class list, package rates. E.g.\n\nDrop-in class — $180 / 60 min\n10-class pack — $1,500\n1-on-1 training — $800 / 60 min',
        education:
          'Paste anything — lesson list, package rates. E.g.\n\nPrivate lesson — $500 / 60 min\n4-lesson pack — $1,800\nGroup class — $200 / 90 min',
        professional:
          'Paste anything — service list, fee schedule. E.g.\n\nInitial consultation — $1,200 / 60 min\nStandard engagement — from $5,000\nMonthly retainer — $8,000',
        generic:
          'Paste anything — product or service list, price sheet.\nFormat each line: Name — Price — Notes (optional)\n\nE.g.\nProduct A — $100\nService B — $200 / 30 min',
      },
      extract: 'Extract',
      extracting: 'Reading your catalog…',
      currencyDetected: 'Detected currency: {currency}',
      colName: 'Name',
      colPrice: 'Price',
      colDescription: 'Description',
      colDuration: 'Duration (min)',
      typeProduct: 'Product',
      typeService: 'Service',
      addRow: 'Add row',
      removeRow: 'Remove row',
      startOver: 'Start over',
      publishNow: 'Publish right after import — the AI can quote and book them at once. Untick to save as drafts.',
      doneDrafts: '{count} item(s) saved as drafts — the AI will not quote them until they are published.',
      importButton: 'Import {count} items',
      importing: 'Importing…',
      doneSummary: 'Imported {count} item(s) successfully.',
      doneFailed: '{count} item(s) could not be imported — you may have hit a plan cap.',
      cancel: 'Cancel',
      close: 'Close',
      errorTooShort: 'Please paste at least a few items so the AI has something to work with.',
      errorExtractFailed: "Couldn't extract from that text. Try fewer items or a cleaner list.",
      errorNoItems: "No items found in that text. Try a clearer format.",
      errorNetwork: 'Network error — please try again.',
    },
  },

  orders: {
    title: 'Orders',
    noOrders: 'No orders yet',
    noOrdersHint: 'Orders will appear here once customers place them',
    orderNumber: 'Order #',
    customer: 'Customer',
    amount: 'Amount',
    status: 'Status',
    time: 'Time',
    actions: 'Actions',
    markPaid: 'Mark as paid',
    markPaidConfirm: 'Are you sure you want to mark this order as paid?',
    confirmOrder: 'Confirm order',
    confirmOrderConfirm: 'Confirm this order? The customer will be notified.',
    completeOrder: 'Complete order',
    completeOrderConfirm: 'Mark this order as complete? The customer will be notified.',
    markShipped: 'Mark as shipped',
    markShippedConfirm: 'Mark this order as shipped?',
    markDelivered: 'Mark as delivered',
    markDeliveredConfirm: 'Mark this order as delivered?',
    cancelOrder: 'Cancel order',
    cancelOrderConfirm: 'Cancel this order? This cannot be undone and stock will be restored.',
    cancelReason: 'Cancel reason (optional):',
    pendingConfirm: 'Pending confirmation',
  },

  customers: {
    title: 'Customers',
    searchPlaceholder: 'Search name, phone...',
    allChannels: 'All channels',
    sortLastSeen: 'Last seen',
    sortEngagement: 'Engagement',
    sortSpent: 'Total spent',
    sortOrders: 'Order count',
    sortName: 'Name',
    noCustomers: 'No customers found',
    noCustomersHint: 'Try adjusting your search or filters',
    noCustomersEmpty: 'Customers will appear here once they message you',
    customerOverview: 'Customer overview',
    count: 'customers',
  },

  bookings: {
    title: 'Bookings',
    listTab: 'Booking list',
    calendarTab: 'Calendar',
    availabilityTab: 'Availability',
    noBookings: 'No bookings yet',
    noBookingsHint: 'Bookings will appear here once customers book via WhatsApp',
    selectHint: 'Select a booking to view details',
    titleFnb: 'Reservations',
    listTabFnb: 'Reservation list',
    availabilityTabFnb: 'Available times',
    noBookingsFnb: 'No reservations yet',
    noBookingsHintFnb: 'Reservations will appear here once customers book via WhatsApp',
    selectHintFnb: 'Select a reservation to view details',
    addBookingFnb: 'New reservation',
    today: 'Today',
    allDay: 'All day',
    upcoming: 'Upcoming',
    service: 'Service',
    duration: 'Duration',
    durationMin: 'min',
    customer: 'Customer',
    phone: 'Phone',
    date: 'Date',
    time: 'Time',
    price: 'Price',
    notes: 'Notes',
    editNotes: 'Edit notes',
    noNotes: 'No notes',
    records: 'Records',
    recordsHint: 'Capture context per booking — easier follow-up + staff handover',
    addRecord: 'Add record',
    recordPlaceholder: 'Today\'s progress / customer reaction / treatment settings / next follow-up...',
    recordEmpty: 'No records yet',
    saveRecord: 'Save',
    cancelRecord: 'Cancel',
    editRecord: 'Edit',
    deleteRecord: 'Delete',
    deleteRecordConfirm: 'Delete this record? Uploaded attachments will also be removed.',
    attachFile: 'Attach',
    attaching: 'Uploading...',
    attachmentTooLarge: 'File exceeds 5MB',
    attachmentRejected: 'Unsupported file type (only images and PDF)',
    attachmentLimit: 'Max 5 attachments per record',
    sessionCounter: 'Session {n} of {m}',
    sessionRemaining: '{n} remaining',
    deposit: 'Deposit',
    depositRequired: 'Deposit {pct}% required',
    depositPaid: 'Deposit collected',
    depositPending: 'Deposit pending',
    cancelReason: 'Cancellation reason',
    viewOrder: 'View order',
    viewCustomer: 'View customer',
    viewConversation: 'View conversation',
    createdAt: 'Created',
    updatedAt: 'Last updated',
    noShow: 'No-show',
    confirmBooking: 'Confirm',
    completeBooking: 'Complete',
    cancelBooking: 'Cancel',
    payment: 'Payment',
    search: 'Search by customer name or phone...',
    staffTab: 'Staff',
    resourcesTab: 'Resources',
    staffTitle: 'Service Staff',
    addStaff: 'Add',
    editStaff: 'Edit',
    staffName: 'Display Name',
    staffNameZh: 'Chinese Name',
    staffTitleField: 'Display title (optional)',
    staffTitlePlaceholder: 'e.g. Dr. Leung / Therapist Stella / Felix sir / Technician Dave',
    staffTitleLegalHint: 'Titles like "Dr.", "Doctor", "Registered Nurse", "Physiotherapist" are legally protected in HK — only use if qualified.',
    staffFallbackLabel: 'Team member',
    staffColor: 'Color',
    staffServices: 'Services',
    staffSchedule: 'Schedule',
    staffBlockedDates: 'Days Off',
    noStaff: 'No service staff yet',
    noStaffHint: 'Add service staff to enable per-person booking assignments',
    staffActive: 'Active',
    staffInactive: 'Inactive',
    deactivateStaff: 'Deactivate',
    activateStaff: 'Activate',
    unassigned: 'Unassigned',
    autoAssign: 'Auto-assign',
    assignedTo: 'Assigned to',
    assignedResources: 'Resources',
    reassign: 'Reassign',
    enableStaffMode: 'Enable service staff assignment',
    disableStaffMode: 'Disable service staff assignment',
    staffModeHint: 'When enabled, customers can choose a specific service staff when booking',
    resources: {
      title: 'Resources',
      description: 'Rooms, chairs, equipment, and machines that get protected from double-booking.',
      addResource: 'Add resource',
      allLocations: 'All locations',
      emptyTitle: 'No resources yet',
      emptyDesc: 'Add rooms, chairs, or equipment to track which ones are in use. Services that need a specific resource will auto-allocate at booking time.',
      gatedTitle: 'Resources is a Business plan feature',
      gatedDesc: 'Track rooms, chairs, and equipment to prevent double-booking at the database level. Ideal for salons with shared stations, clinics with exam rooms, or studios with specialised equipment.',
      gatedBullet1: 'Database-level double-booking protection',
      gatedBullet2: 'Per-location room + equipment tracking',
      gatedBullet3: 'Capacity limits and cleanup buffers',
      gatedBullet4: 'Automatic resource allocation at booking time',
      pitchMedical: 'Medical aesthetics clinics lose bookings the moment the HIFU machine gets double-booked. Resources fixes it at the database level — two customers can never hold the same machine.',
      pitchSalon: 'One treatment room, two stylists, two customers — it happens. Resources track every room and chair so your AI assistant never offers a slot you cannot deliver.',
      pitchRestaurant: 'Group dining, private rooms, counter seats — Resources map them to bookings so the AI only confirms slots you can actually serve.',
      pitchGeneric: 'Protect your bookable rooms, chairs, and equipment from double-booking. The AI assistant only shows slots the system can actually deliver.',
      inlineUpsellTitle: 'This service may need a specific room or machine',
      inlineUpsellBody: 'Upgrade to Business to pin services to specific rooms, chairs, or equipment — booking slots that cannot be allocated are hidden automatically.',
      planBadge: 'Business plan',
      previewLabel: 'Example categories',
      viewPlans: 'See plans',
      needLocation: 'You need at least one active location before adding resources.',
      privateLabel: 'Private',
      cleanupLabel: 'Cleanup: {n} min',
      edit: 'Edit',
      editTitle: 'Edit resource',
      addTitle: 'Add new resource',
      name: 'Name',
      namePlaceholder: 'Room A, Chair 1, HIFU Machine...',
      category: 'Category',
      categoryHint: 'Pick from your resource categories. Manage the list in Categories settings.',
      manageCategories: 'Manage categories',
      noCategoriesYet: 'No categories yet',
      createCategoryFirst: 'Create a resource category before adding resources.',
      location: 'Location',
      selectPlaceholder: '— Select —',
      capacity: 'Capacity',
      capacityHint: 'Parallel bookings. Use 1 for rooms, higher for group studios.',
      cleanup: 'Cleanup time (min)',
      notes: 'Notes',
      notesPlaceholder: 'Internal notes, maintenance schedule, etc.',
      privateCheckbox: 'Private — prefer this for customers asking for a private room',
      errName: 'Name is required',
      errLocation: 'Location is required',
      errCategory: 'Category is required',
      errSaveFailed: 'Failed to save',
      errModuleOff: 'Advanced booking is turned off — switch it on in Settings → Modules first',
      errPlanLocked: 'Rooms & equipment are not included in your plan',
      errCategoryArchived: 'This category is archived — restore it or pick another',
      errNotFound: 'That branch, category or resource no longer exists — reload the page',
      errDeactivateFailed: 'Failed to deactivate',
      deactivateConfirm: 'Deactivate resource "{name}"?',
      cleanupHint: 'Turnover after each booking. The resource stays blocked this long after the service ends.',
      inUseConfirm: '{services} could no longer be booked at this branch without it. Switch it off anyway?',
      futureBookingsNote: '{n} upcoming booking(s) still hold this resource — check them: {list}',
      outOfService: {
        title: 'Out of service',
        hint: 'Under repair or closed for a while? Mark the time — the AI, the booking page and the dashboard stop offering it then. A time range applies on every day of the dates.',
        button: 'Out of service',
        none: 'No upcoming out-of-service time.',
        upcoming: '{n} upcoming',
        add: 'Add',
        from: 'From',
        to: 'To',
        startTime: 'Start time',
        endTime: 'End time',
        timeHint: 'Leave both times empty for whole days.',
        type: 'Type',
        typeMaintenance: 'Maintenance',
        typeUnavailable: 'Unavailable',
        reason: 'Reason',
        reasonPlaceholder: 'e.g. Annual service + calibration',
        allDay: 'All day',
        remove: 'Remove',
        removeConfirm: 'Put "{name}" back in service for {when}?',
        bookedTitle: '{n} upcoming booking(s) use this resource during that time',
        bookedHint: 'They keep their slot — nothing is cancelled. Save anyway, then move or contact them.',
        saveAnyway: 'Save anyway',
        savedWithBookings: 'Saved. {n} booking(s) still hold this resource then — move or contact them.',
        errDates: 'Check the dates — the end must be on or after the start, and not in the past.',
        errTimes: 'Enter both times, with the end after the start.',
        errSpan: 'At most one year — switch the resource off instead.',
        errFailed: 'Could not save',
      },
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving…',
    },
    resourceCategories: {
      title: 'Resource categories',
      resourceCount: '{count} resources',
      description: 'Group your resources (treatment rooms, specific machines, chairs) into categories you can reference from services.',
      addCategory: 'Add category',
      emptyTitle: 'No categories yet',
      emptyDesc: 'Categories are the taxonomy you use to describe your bookable resources — e.g. "Treatment Room", "HIFU Machine", "Hair Station". Add the categories that match how your business actually books.',
      editTitle: 'Edit category',
      addTitle: 'Add new category',
      name: 'Name',
      namePlaceholder: 'Treatment Room, HIFU Machine, ...',
      nameZh: 'Name (Chinese)',
      nameZhPlaceholder: 'Optional localised name',
      categoryDescription: 'Description',
      descriptionPlaceholder: 'What is this category used for?',
      icon: 'Icon',
      color: 'Color',
      sortOrder: 'Sort order',
      save: 'Save',
      cancel: 'Cancel',
      saving: 'Saving…',
      delete: 'Archive',
      deleteConfirm: 'Archive category "{name}"?',
      deleteInUse: 'Cannot archive: {count} active resource(s) still use this category. Move them first.',
      errName: 'Name is required',
      errDuplicate: 'A category with this name already exists',
      errSaveFailed: 'Failed to save',
      errDeleteFailed: 'Failed to archive',
    },
  },

  inquiries: {
    title: 'Customer inquiries',
    noInquiries: 'No inquiries yet',
    noInquiriesHint: 'Customer inquiries recorded by the AI will appear here',
    backToList: 'Back to list',
    customerLabel: 'Customer:',
    phoneLabel: 'Phone:',
    createdLabel: 'Created:',
    resolvedLabel: 'Resolved:',
    viewConversation: 'View conversation →',
    descriptionLabel: 'Description',
    resolutionNote: 'Resolution note',
    resolutionNotePlaceholder: 'Resolution note...',
    markInProgress: 'Mark in progress',
    markResolved: 'Mark resolved',
    updating: 'Updating...',
    reopen: 'Reopen',
    selectInquiry: 'Select an inquiry to view details',
  },

  knowledge: {
    title: 'Knowledge base',
    searchPlaceholder: 'Search knowledge base...',
    noKnowledge: 'No knowledge yet',
    noKnowledgeSearch: 'No matching results',
    noKnowledgeHint: 'Add FAQs, policies, store info, and more. The AI will use them to answer customer questions.',
    categories: { faq: 'FAQ', policy: 'Policy', info: 'Info', general: 'General' },
    aiKeywords: 'AI trained',
    aiGeneratingKeywords: 'AI is learning...',
    aiTraining: 'Learning',
    aiReady: 'Ready',
    editKnowledge: 'Edit knowledge',
    addKnowledge: 'Add knowledge',
    editHint: 'Edit the content below. The AI will automatically update search keywords.',
    addHint: 'After adding, the AI will automatically generate search keywords.',
    titleField: 'Title',
    titlePlaceholder: 'e.g. Return policy',
    contentField: 'Content',
    contentPlaceholder: 'The AI will use this content to answer customer questions',
    categoryField: 'Category',
    updatedAt: 'Updated:',
    selectKnowledge: 'Select an entry to view details',
    import: 'Import',
    importTitle: 'Import Knowledge',
    importSourceUrl: 'URL',
    importSourceFile: 'File',
    importSourceText: 'Paste Text',
    importUrlPlaceholder: 'Enter a web page URL, e.g. https://myshop.com/faq',
    importTextPlaceholder: 'Paste text content directly (FAQ, policies, service descriptions, etc.)',
    importFileDrop: 'Drop a file or click to select',
    importFileSupported: 'Supports PDF, Word, PowerPoint, TXT, Images',
    importExtracting: 'Extracting content...',
    importExtractingHint: 'AI is reading and structuring your content',
    importPreviewTitle: 'Preview extracted content',
    importPreviewEdit: 'You can edit the content if anything is incorrect',
    importSplitting: 'Splitting into entries...',
    importSplittingHint: 'AI is splitting the content into individual knowledge entries',
    importReviewTitle: 'Review knowledge entries',
    importReviewCount: 'AI has organized {count} knowledge entries',
    importSelectAll: 'Select all',
    importAsDraft: 'Import as Draft',
    importPublish: 'Publish Now',
    importPublishFailed: '{n} entries could not be saved. They are still selected — try again.',
    importLikelyDuplicate: 'Looks like the existing "{title}" — left unticked; publish only if it is different.',
    importBack: 'Back',
    importNext: 'Continue',
    importStart: 'Start Extraction',
    importError: 'Import failed',
    importNoContent: 'Could not extract any valid content',
    tabEntries: 'Knowledge',
    tabSources: 'Sources',
    sourcesEmpty: 'No import sources',
    sourcesEmptyHint: 'Import files or web pages — AI will organize them into your knowledge base',
    sourceEntries: '{count} entries',
    sourceProcessing: 'Processing...',
    sourceFailed: 'Processing failed',
    sourceDeleteTitle: 'Delete source',
    sourceDeleteKeep: 'Delete source only',
    sourceDeleteAll: 'Delete with all entries',
    sourceDeleteKeepHint: 'Keep the generated knowledge entries',
    sourceDeleteAllHint: '{count} knowledge entries from this source will also be deleted',
    filterBySource: 'Source',
    sourceFilterAll: 'All Sources',
    sourceFilterManual: 'Manual',
    sourceFilterConversation: 'From Conversations',
    sourceBadgeManual: 'Manual',
    sourceBadgeFile: 'File',
    sourceBadgeUrl: 'URL',
    sourceBadgeConversation: 'Chat',
    sourceBadgeImage: 'Image',
    sourceBadgeText: 'Text',
    entryFromSource: 'Source',
    viewSource: 'View source',
  },


  usage: {
    title: 'Usage',
    loadFailed: 'Failed to load',
    planNames: { free: 'Free', starter: 'Starter', pro: 'Pro', business: 'Business', enterprise: 'Enterprise' },
  },

  plan: {
    limitReachedTitle: 'Not included in your plan',
    scaleCapTitle: 'Limit reached',
    limitReachedBody: 'Your {plan} plan does not include {feature}. Upgrade to unlock it instantly.',
    scaleCapBody: 'You have reached your {plan} plan limit on {feature}. Upgrade to {target} for more.',
    upgradeCta: 'Upgrade to {plan}',
    manageOnWeb: 'Manage your plan at app.novixone.ai in your browser',
    quotaReachedBody: "This month's {feature} quota has been reached.",
    quotaUsage: '{used} / {limit} used this month',
    unlimited: 'Unlimited',
    creditsExhaustedTitle: 'AI credits used up',
    creditsExhaustedBody:
      'Your AI credits for this period have run out, so AI features are paused. Buy a credit pack or upgrade your plan to keep going.',
    creditsExhaustedCta: 'Go to billing',
    featureNames: {
      monthlyReplies: 'AI replies',
      monthlyKnowledgePagesImported: 'knowledge import pages',
      maxTeamMembers: 'team members',
      maxWhatsappNumbers: 'WhatsApp numbers',
      maxProducts: 'products',
      maxKnowledgeEntries: 'knowledge entries',
      maxCustomerSegments: 'customer segments',
      maxAutomationRules: 'automation rules',
      customRoles: 'custom roles',
      knowledgeAdvancedImport: 'advanced knowledge import (PDF / URL / OCR)',
      cohortFunnelAnalytics: 'cohort & funnel analytics',
      conversationalAnalytics: 'conversational analytics (ask your data)',
      integrationEvents: 'event delivery (webhooks)',
      publicApiRead: 'API access',
      publicApiWrite: 'API write access',
      aiActions: 'AI actions',
      integrationConnectors: 'connectors',
      providerContract: 'connecting your own system',
      customDomain: 'custom domain',
    },
  },

  login: {
    heroTitle1: 'Turn customer',
    heroTitle2: 'conversations into',
    heroTitle3: 'revenue',
    heroSubtitle: 'AI that sells, books and supports across WhatsApp, LINE and your website chat — 24/7.',
    features: {
      aiSupport: 'Omnichannel AI agent',
      aiSupportDesc: '24/7 multilingual auto-reply — closes sales, answers FAQs, books appointments',
      orderViaChat: 'Order in-chat',
      orderViaChatDesc: 'Customers order directly inside WhatsApp or LINE — no redirects',
      memory: 'Customer memory',
      memoryDesc: 'AI remembers every customer\u2019s preferences for personalised service',
    },
    orEmail: 'Or sign in with email',
    emailLabel: 'Email address',
    emailPlaceholder: '',
    checkEmail: 'Check your email',
    emailSentTo: 'A sign-in link has been sent to',
    clickLink: 'Click the link in the email to sign in',
    backToLogin: '← Back to sign in',
    signInTitle: 'Sign in',
    signInSubtitle: 'Sign in to your NovixOne account',
    signUpTitle: 'Create your account',
    signUpSubtitle: 'Set up NovixOne in about 5 minutes',
    googleSignIn: 'Sign in with Google',
    googleSignUp: 'Sign up with Google',
    orEmailSignUp: 'Or sign up with email',
    freePlanBadge: 'Free plan: 100 active customers a month · No credit card',
    joinTeamTitle: 'Join your team',
    joinTeamSubtitle: 'A shop invited you to NovixOne. Create your account, then accept the invitation to start handling conversations, bookings and customers.',
    joinTeamCardSubtitle: 'Use the email address the invitation was sent to — you will come back to the invitation afterwards.',
    bookDemo: 'Book a demo',
    worksWith: 'Works with',
    webChat: 'Website chat',
    trustLine: 'Encrypted data · Privacy by design · Your data, your control',
    passwordHint: 'At least 8 characters',
    recommendedBadge: 'Recommended',
    welcomeBack: 'Welcome back',
    verifyEmailTitle: 'Verify your email',
    verifyEmailSentTo: 'We\u2019ve sent a verification link to',
    verifyEmailHint: 'Click the link in the email to confirm your identity, then you can continue setting up NovixOne.',
    sending: 'Sending...',
    sendLink: 'Send sign-in link',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Enter your password',
    signIn: 'Sign in',
    signUp: 'Create account',
    signingIn: 'Signing in...',
    signingUp: 'Creating account...',
    noAccount: "Don't have an account?",
    hasAccount: 'Already have an account?',
    forgotPassword: 'Forgot password?',
    switchToSignUp: 'Sign up',
    switchToSignIn: 'Sign in',
    orMagicLink: 'Or use a magic link instead',
    orPassword: 'Or use email & password',
    forgotPasswordTitle: 'Reset your password',
    forgotPasswordSubtitle: "Enter your email and we'll send you a reset link",
    sendResetLink: 'Send reset link',
    sendingReset: 'Sending...',
    resetEmailSent: 'Reset link sent',
    resetEmailSentDesc: 'Check your email for a password reset link',
    backToSignIn: 'Back to sign in',
    resetPasswordTitle: 'Set new password',
    resetPasswordSubtitle: 'Enter your new password below',
    newPasswordLabel: 'New password',
    newPasswordPlaceholder: 'At least 6 characters',
    confirmPasswordLabel: 'Confirm password',
    confirmPasswordPlaceholder: 'Re-enter your password',
    updatePassword: 'Update password',
    updatingPassword: 'Updating...',
    passwordMismatch: 'Passwords do not match',
    passwordTooShort: 'Password must be at least 6 characters',
    passwordResetSuccess: 'Password updated successfully',
    passwordResetRedirect: 'Redirecting to dashboard...',
    termsAgreement: 'By creating an account, you agree to our',
    legalLinks: { terms: 'Terms of Service', privacy: 'Privacy Policy', aup: 'Acceptable Use Policy' },
    trustEncryption: 'Data encryption',
    trustSecurity: 'Secure',
    trustSetup: 'Quick setup',
    trustSetupTime: '5 min',
    trustAutoReply: 'AI auto-reply',
    trustAutoReplyTime: '24/7',
    footer: '© 2026 Novix Star Limited · novixone.ai',
  },

  onboarding: {
    title: 'NovixOne Setup',
    subtitle: 'Set up your AI assistant in under a minute',
    setupComplete: 'Setup complete!',
    redirecting: 'Redirecting to dashboard...',
    redirectHint: 'Follow the setup checklist to complete the rest',
    settingUp: 'Setting things up...',
    // Wizard steps
    step1Title: 'Business Profile',
    step1Subtitle: 'Tell us about your business',
    step2Title: 'AI Assistant',
    step2Subtitle: 'Customize your AI persona',
    step3Title: 'Confirm & Launch',
    step3Subtitle: 'Review and start',
    // Step 1 fields
    businessNameLabel: 'Business name',
    businessNamePlaceholder: 'e.g. Ming Hin Home',
    industryLabel: 'Industry',
    regionLabel: 'Primary business region',
    // Step 2 fields
    aiNameLabel: 'AI assistant name',
    aiNamePlaceholder: 'Leave blank to use your business name',
    toneLabel: 'AI conversation style',
    toneFriendly: 'Friendly',
    toneProfessional: 'Professional',
    toneCasual: 'Casual',
    // Step 3
    summaryTitle: 'Setup Summary',
    editButton: 'Edit',
    // Navigation
    next: 'Next',
    back: 'Back',
    launch: 'Launch',
    // ── V3 conversational onboarding ──
    describeTitle: 'Tell us about your business',
    describeSubtitle: 'A sentence or two is enough — what you do, where, and what makes it yours.',
    describeHint: 'Need inspiration? Tap one of these:',
    describeContinue: 'Continue',
    describeAnalyzing: 'Understanding your business…',
    exampleChipFnb: 'Food & drink',
    exampleChipSalon: 'Beauty / salon',
    exampleChipRetail: 'Retail',
    exampleChipProfessional: 'Professional services',
    exampleChipHealth: 'Health & wellness',
    exampleChipEducation: 'Education',
    exampleChipOther: 'Something else',
    reviewTitle: 'Review & edit',
    reviewSubtitle: 'Check what I understood. Green means I\'m confident; amber means please verify.',
    reviewOriginalLabel: 'You said:',
    reviewReDescribe: '← Re-describe',
    aiInferredBadge: 'AI inferred',
    verifyBadge: 'Please verify',
    subTypeLabel: 'Sub-type',
    subTypePlaceholder: 'e.g. gel nail + retail polish',
    clarificationHint: 'One quick question:',
    flaggedTitle: 'We need to take a look',
    flaggedBody:
      'Something in your description needs a human review before we can set up your account. Our team will reach out shortly.',
    extractionFailedHint:
      "Couldn't auto-fill from your description — please fill in the fields below manually.",
  },

  activate: {
    heroTitle: 'Set up your AI assistant',
    heroSubtitle:
      "Your account is ready. Work through the steps below so your AI can handle customer messages well, then connect WhatsApp to go live.",
    progressLabel: '{done} of {total} complete',
    recommendedBadge: 'Recommended',
    doneBadge: 'Done',
    catalogTitleCommerce: 'Add your products',
    catalogTitleBooking: 'Add your services',
    catalogDescription:
      'Paste your menu or service list. AI needs this to quote prices and take orders.',
    catalogDone: '{count} items ready',
    hoursTitle: 'Set booking hours',
    hoursDescription: 'When can customers book? AI uses this to check availability.',
    hoursDone: '{count} time slots configured',
    policiesTitle: 'Configure your policies',
    policiesDescription:
      'Cancellation window, return rules, deposits. Leave blank for anything you handle case-by-case — AI will escalate to you.',
    paymentTitle: 'Payment setup',
    paymentDescription: 'Connect Stripe or list your local payment methods.',
    paymentDone: 'Payment methods configured',
    knowledgeTitle: 'Teach your AI',
    teachTitle: 'Teach your AI in one step',
    teachSubtitle: 'Paste your website, or your FAQ / price list. We turn it into answers your AI can use — you review them in the knowledge base.',
    teachUrlTab: 'Website link',
    teachTextTab: 'Paste text',
    teachUrlPlaceholder: 'https://your-shop.com',
    teachTextPlaceholder: 'Opening hours, prices, address, how to book, payment methods, cancellation policy…',
    teachSubmit: 'Teach my AI',
    teachSubmitting: 'Reading…',
    teachStarted: 'Got it — your AI is reading it now. The answers appear in the knowledge base within a minute or two.',
    teachFailed: 'That could not be read. Try pasting the text instead.',
    teachViewKnowledge: 'Open knowledge base',
    knowledgeDescription:
      "FAQs and info specific to your business. You can also paste existing materials — or let AI capture answers from your real replies after you go live.",
    knowledgeDone: '{count} knowledge entries',
    sandboxTitle: 'Test in sandbox',
    sandboxDescription: 'Chat with your AI as if you were a customer — at least 3 messages helps catch real issues.',
    sandboxProgress: '{count}/{total} test messages sent — keep going.',
    sandboxDone: "You've tested — great, any issues to address?",
    goLiveTitle: 'Ready to go live?',
    goLiveDescription:
      'Connect WhatsApp to start handling real customers.',
    goLiveWarning: '{count} recommended step(s) still pending — you can still go live, but AI will escalate more often.',
    goLiveButton: 'Connect WhatsApp',
    skipToDashboard: 'Skip for now — take me to the dashboard',
    noSellingModulesHint: 'Orders and bookings are off, so there are no product, hours or payment steps.',
    manageFeaturesLink: 'Manage features',
    liveTitle: "You're live!",
    liveSubtitle:
      "Your AI is now handling real customer messages on WhatsApp.",
    liveTestCta: 'Send a test message',
    liveDashboardCta: 'Go to dashboard',
    liveRemainingHint: '{pending} setup step(s) left — each one means the AI hands fewer chats to you.',
  },

  getStarted: {
    title: 'Welcome to NovixOne 👋',
    subtitle: "Before you start, please take a minute to confirm the terms below. We'll help you set up your merchant account next.",
    legalIntro: 'This service is provided by Novix Star Limited. Please read and agree to the following documents:',
    docs: { tos: 'Terms of Service', privacy: 'Privacy Policy', aup: 'Acceptable Use Policy', dpa: 'Data Processing Addendum' },
    checkbox1Prefix: 'I have read and agree to the ',
    checkbox1Sep: ', ',
    checkbox1And: ', ',
    checkbox1End: ' and ',
    authorityInline: 'By signing you confirm you have authority to represent your business',
    marketingOptIn: 'Send me NovixOne product updates, offers and tips',
    marketingOptInHint: 'Unsubscribe anytime from Settings',
    optionalLabel: 'Optional',
    button: 'Agree & Continue',
    ctaRemaining: '{count} more to confirm',
    submitting: 'Processing...',
    error: 'Something went wrong. Please try again.',
    progressLabel: 'Setup progress',
    progressStep1: 'Agree',
    progressStep2: 'Business',
    progressStep3: 'AI setup',
    progressStep4: 'Launch',
    supportHint: 'Questions?',
    supportEmail: 'support@novixone.ai',
  },

  legalBanner: {
    title: "We've updated our legal terms",
    desc: 'Please review and re-accept the latest Terms of Service, Privacy Policy, AUP and DPA.',
    review: 'Review',
    accept: 'I agree',
    accepting: 'Processing...',
    error: 'Failed, please retry',
  },

  invite: {
    invalidLink: 'Invalid invitation link',
    teamInvite: 'Team invitation',
    joinTeam: 'Join team',
    inviteMessage: 'You have been invited to join a NovixOne team.',
    clickToJoin: 'Click the button below to accept.',
    accept: 'Accept invitation',
    joining: 'Joining...',
    successJoined: 'Successfully joined {name}!',
    redirecting: 'Redirecting to dashboard...',
    backToLogin: 'Back to sign in',
    invitedBy: '{inviter} invited you to join',
    forEmail: 'Invitation for {email}',
    createAccount: 'Create an account and join',
    signIn: 'Already have an account? Sign in',
    wrongAccount: "You are signed in as {current}, but this invitation is for {invitee}. Sign in with that email to accept it.",
    switchAccount: 'Sign out and switch account',
    errorInvalid: 'This invitation has expired or was withdrawn — ask the shop for a new one.',
    errorMismatch: 'This invitation is for a different email address — sign in with the invited email.',
    errorGeneric: 'Could not join the team. Please try again.',
  },

  pay: {
    successTitle: 'Payment successful',
    successMessage: 'Thank you for your purchase! We have received your payment.',
    orderNumber: 'Order #:',
    successHint: 'You can close this page and return to WhatsApp to continue the conversation.',
    cancelTitle: 'Payment cancelled',
    cancelMessage: 'Your payment was not completed. To try again, please return to WhatsApp and contact the merchant.',
    cancelHint: 'You can close this page.',
  },

  setup: {
    title: 'Complete setup',
    progress: '{done}/{total} completed · {pct}%',
    addServices: 'Add services',
    addProducts: 'Add products / services',
    productsDone: '{count} added',
    productsHint: 'The AI needs to know what you sell to make recommendations',
    paymentSetup: 'Set up payment',
    paymentStripe: 'Stripe online payments connected',
    paymentLocal: 'Manual methods configured',
    paymentHint: 'Connect Stripe or add manual methods (transfer, mobile wallet, cash, etc.)',
    knowledgeFaq: 'Add FAQs',
    knowledgeBase: 'Add knowledge base',
    knowledgeDone: '{count} entries added',
    knowledgeHintSupport: 'FAQs, guides, and more help the AI answer accurately',
    knowledgeHint: 'FAQs, return policies, and more help the AI answer accurately',
    knowledgeDraftsPending: '{count} captured draft(s) awaiting your review',
    bookingSetup: 'Set up booking slots',
    bookingDone: '{count} slots configured',
    bookingHint: 'Set your availability so customers can book through the AI',
    askCopilot: 'Set up with AI assistant',
    sandboxTest: 'Test AI conversation',
    sandboxDone: 'AI tested in sandbox',
    sandboxHint: 'Try your AI as a customer before going live',
    segmentSetup: 'Set up customer segments',
    segmentHint: 'Group customers by behavior for targeted outreach',
    segmentCount: (n) => (n === 1 ? '1 segment created' : `${n} segments created`),
    outreachSetup: 'Configure outreach campaigns',
    outreachDone: 'Outreach templates ready',
    outreachHint: 'Set up automated follow-ups for abandoned carts and lapsed customers',
  },

  sandbox: {
    title: 'AI Sandbox',
    subtitle: 'Test your AI assistant as a customer. All data here is isolated from your live environment.',
    inputPlaceholder: 'Type a message as a customer...',
    newConversation: 'New conversation',
    clearAll: 'Clear all test data',
    clearConfirm: 'This will delete ALL sandbox test data including conversations, orders, bookings, inquiries and customer records. Continue?',
    cleared: 'All test data cleared',
    creditsRemaining: '{n} credits left',
    creditsExhausted: "You're out of AI credits. Top up to keep testing.",
    noMessages: 'Send a message to start testing your AI',
    banner: 'Sandbox mode — conversations and data here won\'t affect your live environment',
    history: 'Test history',
  },

  push: {
    title: 'Enable push notifications',
    description: 'Get notified instantly when a customer needs human assistance, so you never miss a conversation.',
    allow: 'Allow notifications',
    dismiss: 'Dismiss',
    integrationApprovalTitle: 'Approval needed: {action}',
  },

  copilot: {
    title: 'AI Assistant',
    greeting: 'How can I help?',
    clearChat: 'Clear chat',
    welcomeTitle: 'AI Assistant',
    welcomeHint: 'Manage your business with natural language, e.g. "Add a service called Aircon cleaning, $480", "How many bookings today?", or upload a product list / price list image',
    uploadImage: 'Upload image',
    pendingUpload: 'Pending upload',
    analyzeImage: 'Please analyse this image',
    imageInputPlaceholder: 'Describe the image or just send...',
    inputPlaceholder: 'Type a message...',
    morningBriefing: "Give me today's briefing",
    actionNotDoneYet: 'Nothing has been done yet.',
    actionConfirm: 'Confirm',
    actionCancel: 'Cancel',
    actionChallengeMismatch: 'That does not match. Check the figure and try again.',
    actionNotAllowed: 'You no longer have permission for this, or the feature is off.',
    actionError: 'Could not complete that. Please try again.',
    uploadFailed: 'Upload failed. Images must be under 4 MB.',
    workingSteps: '{n} steps so far',
    continueRun: 'Continue',
    batchApprove: 'Approve {n} selected',
    batchCancelAll: 'Cancel all',
    batchTitle: '{n} items to confirm',
    batchFinished: '{ok} done, {failed} did not go through.',
    batchDeclined: 'All cancelled. Nothing was run.',
    creditsUsed: '{n} credits used',
    stop: 'Stop',
    actionTainted: 'The AI suggested this after reading text someone else wrote, such as a customer message or a file. Check that it is what you want.',
    fileRejected: 'This file cannot be used. Excel or CSV files up to 25 MB work.',
    importThisFile: 'Please import the products in this file.',
  },

  settings: {
    title: 'Settings',
    locations: {
      title: 'Locations',
      description: 'Manage the branches / stores that power your booking flow. Each location has its own business hours, staff, and timezone.',
      defaultBadge: 'Default',
      additionalBranches: 'Additional branches',
      multiBranchLockedTitle: 'More branches need the Pro plan or above',
      multiBranchLockedBody: 'Pro manages up to 3 branches and Business up to 10, each with its own opening hours, staff schedules and resources.',
      branchLimitReachedTitle: 'Branch limit reached',
      branchLimitReachedBody: 'Upgrade to Enterprise for unlimited branches, or delete an existing branch first.',
      viewPlans: 'View plans',
      addBranch: 'Add branch',
      services: 'Services',
      edit: 'Edit',
    },
    channels: {
      title: 'Channels',
      description:
        'Connected messaging channels — WhatsApp numbers and LINE accounts. Manage each channel\'s name, AI agent, and default sending number.',
      emptyState: 'No channels connected yet.',
      emptyStateHint: 'Connect a WhatsApp number or LINE Official Account to let the AI reply to your customers.',
      connectChannel: 'Connect channel',
      connectWhatsApp: 'Connect WhatsApp',
      connectLine: 'Connect LINE',
      chooseChannelTitle: 'Choose a channel to connect',
      whatsappOptionHint: 'WhatsApp Business number via Meta sign-in',
      lineOptionHint: 'Needs a LINE Official Account (free). About 10 minutes, with a step-by-step guide',
      activeBadge: 'Active',
      sandboxBadge: 'Sandbox',
      defaultBadge: 'Default',
      defaultHint: 'Broadcasts and other messages not tied to a conversation are sent from the default channel.',
      setDefault: 'Set as default',
      connectedOn: 'Connected {date}',
      editLabel: 'Edit label',
      labelPlaceholder: 'e.g. Main store, Bookings line',
      agentLabel: 'AI agent',
      agentInherit: 'Merchant default',
      disconnect: 'Disconnect',
      disconnecting: 'Disconnecting…',
      disconnectTitle: 'Disconnect {name}?',
      disconnectBody: 'The AI will stop replying on this channel immediately. Conversation history is kept, and you can reconnect at any time.',
      disconnectDefaultNote: 'This is your default channel — the earliest-connected remaining channel will take over as default.',
      loadError: 'Could not load channels.',
      waTitle: 'Connect WhatsApp Business',
      waIntro: 'You will sign in with Facebook and pick (or create) your WhatsApp Business number in a popup window.',
      waPrereq: 'Have ready: a Meta Business account, and a phone number not already registered to another WhatsApp account.',
      waContinue: 'Continue with Facebook',
      waWaiting: 'Waiting for Facebook…',
      waWaitingHint: 'Complete the steps in the Facebook window. It closes automatically when done.',
      waCancelled: 'Facebook authorisation was cancelled.',
      waSuccessTitle: 'Number connected',
      waSuccessBody: '{number} is connected. The AI starts replying to customers on it immediately.',
      waConnectAnother: 'Connect another number',
      error: 'Could not save. Please retry.',
    },
    templates: {
      title: 'WhatsApp templates',
      description:
        'Message templates for broadcasts, automated follow-ups, and system notifications (booking confirmations, reminders). Templates must be approved by Meta before they can be sent.',
      manageLink: 'Manage WhatsApp templates',
      sync: 'Sync status',
      syncing: 'Syncing…',
      syncDone: 'Synced {n} templates from Meta',
      syncFailed: 'Sync failed. Please retry.',
      loadError: 'Could not load templates.',
      emptyNotConnected: 'Connect a WhatsApp number first',
      emptyNotConnectedCta: 'Go to Channels',
      emptyNoTemplates: 'No templates yet.',
      emptyNoTemplatesHint: 'Run a sync to register the standard templates with Meta and pull their status.',
      systemGroup: 'System templates',
      customGroup: 'Custom templates',
      statusApproved: 'Approved',
      statusPending: 'In review',
      statusPendingHint: 'Usually minutes to 24 hours',
      statusRejected: 'Rejected',
      statusPaused: 'Paused',
      statusPausedHint: 'Paused by Meta due to a low quality rating — resumes automatically',
      statusDisabled: 'Disabled',
      qualityLabel: 'Quality',
      qualityGreen: 'Good',
      qualityYellow: 'Fair',
      qualityRed: 'Poor',
      categoryUtility: 'Transactional',
      categoryUtilityHint: 'Order / booking related. Free inside a 24-hour customer session.',
      categoryMarketing: 'Marketing',
      categoryMarketingHint: 'Promotions and campaigns. Billed per message.',
      bodyLabel: 'Message body',
      variablesLabel: 'Variables',
      languageLabel: 'Language',
      wabaLabel: 'WhatsApp Business Account',
      lastSynced: 'Last synced {date}',
      rejectedReason: 'Rejection reason',
      viewDetail: 'View details',
      detailMetaName: 'Template name at Meta',
      newTemplate: 'New template',
      editTemplate: 'Edit',
      systemBadge: 'System notice',
      systemEditHint:
        'This is the platform’s standard wording. Once you edit it, this template stops following future system updates.',
      editTemplateLang: 'Edit {language} version',
      pickerNoApproved: 'No approved templates yet. Create one and wait for Meta approval before sending.',
      pickerAutoFilled: 'Filled automatically per recipient',
      pickerLanguageAuto: 'Language is picked automatically per customer',
      langNames: {
        zh_HK: 'Traditional Chinese',
        zh_CN: 'Simplified Chinese',
        en: 'English',
      },
      addTranslation: 'Add translation',
      variantAdded: '{language} translation submitted for review',
      variantAddFailed: 'Could not add the translation. Please retry.',
      languageExists: 'This template already has that language.',
      otherLanguages: 'Other languages',
      searchLanguage: 'Search languages…',
      noLanguageResults: 'No matching language',
      operatingLanguages: 'Operating languages',
      operatingLanguagesHint:
        'Languages your business serves customers in. Template coverage, translation suggestions and default sends follow this list — the first one is your primary.',
      primaryLang: 'Primary',
      makePrimary: 'Set as primary',
      addLanguage: 'Add language',
      removeLangTitle: 'Remove {language}?',
      removeLangBody:
        'Existing approved templates in this language are kept and can still be sent — it just stops counting toward coverage.',
      provisionSystemCheckbox:
        'Also submit all system templates in {language} for Meta review (one click, recommended)',
      provisionNonRegistryHint:
        'System templates can be added in this language one by one from each template card — AI translation drafts the copy for your review.',
      insightTitle: 'Language insights',
      insightLine: '{n} customers prefer {language}, which you don’t support yet.',
      insightAdd: 'Add {language}',
      insightDismiss: 'Dismiss',
      bulkFillLine: '{n} system templates have ready-made official {language} copy — submit them for Meta review.',
      bulkFillCta: 'Review & submit',
      bulkFillProgress: 'Submitting {i}/{n}…',
      bulkFillDone: '{n} templates submitted for Meta review.',
      bulkFillPartial: '{ok} submitted, {fail} failed — retry from the template cards.',
      bulkFillNonRegistryHint:
        '{n} system templates have no {language} version — add them one by one from each card (AI translation drafts the copy).',
      bulkReviewTitle: 'Submit {n} system templates in {language}',
      bulkReviewNote:
        'This is the official system copy. Submitting sends it to Meta for review (usually minutes to a few hours); the matching features go live automatically once approved.',
      bulkReviewConfirm: 'Submit {n} for Meta review',
      initializing: 'Setting up system templates on your WhatsApp account…',
      ghostTemplatesNote: '{n} more templates belong to a disconnected WhatsApp number and are not shown here.',
      provisionAfterConnectHint: 'Once WhatsApp is connected, system templates are submitted automatically in your operating languages.',
      authExpired: 'WhatsApp authorisation has expired — templates cannot sync or send. Reconnect your WhatsApp account.',
      detailPreviewLabel: 'What your customer receives',
      technicalDetails: 'Technical details',
      qualityGood: 'Good',
      qualityFair: 'Fair',
      qualityPoor: 'Poor',
      lastSyncedLabel: 'Last synced',
      pickerNoMarketing: 'You have no approved marketing templates yet — broadcasts can only be sent with a Marketing-category template.',
      customEmptyTitle: 'Your marketing message templates',
      customEmptyBody:
        'Once a customer has been silent for over 24 hours, WhatsApp only allows Meta-approved templates. Broadcasts, automations and AI assistant follow-ups all pull from here — create your own marketing content.',
      addVariantTitle: 'Add {language} version',
      addVariantReviewNote:
        'Submitting sends this copy to Meta for review (usually minutes to a few hours). It goes live automatically once approved.',
      addVariantSubmit: 'Submit for Meta review',
      addVariantGoEditor: 'Open editor',
      addVariantEditorHint:
        'This version is written in the editor — AI translation drafts it from your existing version for you to review before submitting.',
      manageLanguages: 'Manage',
      removeLangAction: 'Remove',
      system: {
        booking_confirmation: {
          title: 'Booking confirmation',
          trigger: 'Sent automatically the moment a customer books',
        },
        appointment_reminder_24h: {
          title: 'Day-before reminder',
          trigger: 'Sent automatically 24 hours before the appointment',
        },
        appointment_reminder_2h: {
          title: 'Two-hour reminder',
          trigger: 'Sent automatically 2 hours before the appointment',
        },
        order_confirmed: {
          title: 'Order confirmation & payment details',
          trigger: 'Sent automatically the moment a customer places an order',
        },
        payment_received: {
          title: 'Payment received',
          trigger: 'Sent automatically once you confirm a payment',
        },
        payment_reminder: {
          title: 'Payment reminder',
          trigger: 'Sent automatically when an order paid upfront is still unpaid after 4 hours (between 09:00 and 21:00 your time)',
        },
        order_cancelled: {
          title: 'Order cancelled',
          trigger: 'Sent automatically when an order is cancelled',
        },
        review_request: {
          title: 'Ask for a review',
          trigger: 'Sent automatically after a completed booking or order',
        },
        booking_slot_available: {
          title: 'Waitlist slot opened',
          trigger: 'You send this when a cancellation frees a slot a customer was waiting for',
        },
        product_back_in_stock: {
          title: 'Back-in-stock alert',
          trigger: 'Sent automatically when a waitlisted product is restocked',
        },
      },
      groups: {
        booking: 'Booking notices',
        orders: 'Orders & payments',
        relationship: 'Customer relationship',
        custom: 'Messages you write',
        customHint: 'Broadcasts, automations and AI follow-ups all send from here.',
        customTrigger: 'You send this yourself — broadcasts, automations, AI follow-ups',
        notUsedByYourBusiness: '{group} — {n} notices your business does not use yet',
      },
      state: {
        working: 'Working',
        workingLang: 'Working ({languages})',
        inReview: 'In review at Meta · usually minutes to 24 hours',
        fallbackWarn: '{missing} customers will receive {fallback}',
        fallbackFix: 'Add {language}',
        cannotSend: 'Cannot send — no language approved yet',
        cannotSendFix: 'Set up',
        rejected: 'Rejected by Meta — needs changes',
        rejectedFix: 'Fix & resubmit',
      },
      attention: {
        title: '{n} notices need your attention',
        filterAll: 'All ({n})',
        filterNeedsAction: 'Needs attention ({n})',
        allClear: 'Every notice is working',
      },
      cost: {
        free: 'Free',
        freeHint:
          'Order and booking notices. Free whenever the customer has replied in the last 24 hours.',
        paid: 'Charged',
        paidHint: 'Promotional content. Meta charges you for every message sent.',
      },
      intro: {
        title: 'Why does WhatsApp have to approve my messages?',
        body1:
          'Once a customer has been silent for more than 24 hours, WhatsApp only lets you reach them with wording Meta approved in advance.',
        body2:
          'That is what these are. Each one is reviewed once — usually within minutes to 24 hours — and can then be sent forever.',
        body3: 'Order and booking notices are free. Promotional messages are charged per send.',
        dismiss: 'Got it',
      },
      searchPlaceholder: 'Search notices…',
      noSearchResults: 'No matching notice',
      editor: {
        newTitle: 'New WhatsApp template',
        editTitle: 'Edit template',
        displayName: 'Template name',
        displayNamePlaceholder: 'e.g. Birthday offer',
        categoryLabel: 'Purpose',
        bodyPlaceholder: 'Type your message. Insert variables with the buttons below — never type {{1}} manually.',
        insertVariable: 'Insert variable',
        customVariable: 'Custom…',
        customVariablePrompt: 'Variable name (lowercase letters / underscores, e.g. coupon_code)',
        footerLabel: 'Footer (optional)',
        footerPlaceholder: 'e.g. Reply STOP to opt out',
        buttonsLabel: 'Quick reply buttons (optional)',
        buttonsHint: 'Up to 3 buttons, 25 characters each.',
        addButton: 'Add button',
        samplesTitle: 'Sample values',
        samplesHint: 'Shown to Meta reviewers and used in the preview.',
        previewTitle: 'Preview',
        submit: 'Submit for review',
        submitting: 'Submitting…',
        resubmit: 'Save & resubmit for review',
        reviewNote: 'Meta review usually takes minutes to 24 hours. You will be notified of the result.',
        editQuotaNote: 'Approved templates can be edited once per 24 hours (max 10 edits / 30 days). The previous version keeps sending during review.',
        pendingLocked: 'This template is in review and cannot be edited until Meta decides.',
        deleteTemplate: 'Delete template',
        deleteConfirmTitle: 'Delete this template?',
        deleteConfirmBody: 'It is removed from Meta as well. Broadcasts and automations using it will stop sending.',
        metaRejected: 'Meta rejected the submission: {message}',
        unknownVariables: 'System templates can only use their built-in variables: {allowed}',
        submitted: 'Submitted for review',
        variantTitle: 'Add translation',
        variantSubtitle: '{key} · {language}',
        variantNote: 'Translations keep the original template\'s variables — only the wording changes. Each language is reviewed by Meta separately.',
        translateFrom: '✨ AI translate from {language}',
        moreLanguages: 'More languages…',
        translating: 'Translating…',
        aiDraftTitle: 'Let AI write it',
        aiDraftHint: 'Describe what you want to send in one sentence — AI drafts a compliant marketing template for you to review and edit before submitting.',
        aiDraftPlaceholder: 'e.g. Grand opening — 20% off everything, reply to book',
        aiDraftCta: 'Draft',
        aiDraftLoading: 'Drafting…',
        aiDraftFailed: 'AI draft failed — try again or write it yourself.',
        translateFailed: 'AI translation failed — write it manually or retry.',
        variablesMismatch: 'Variables must match the original template: {expected}',
        sectionBasics: 'Basics',
        sectionContent: 'Message content',
        sectionChecklist: 'Pre-flight check',
        startTitle: 'How do you want to start?',
        startHint: 'You can change everything afterwards — nothing is sent to Meta until you submit.',
        startAi: 'Let AI write it',
        startAiHint: 'Describe it in one sentence',
        startBlank: 'Write it myself',
        startBlankHint: 'Start from a blank message',
        startCopy: 'Copy an existing template',
        startCopyHint: 'Start from one you already have',
        copySourceTitle: 'Copy from which template?',
        copySourceEmpty: 'No existing templates to copy from yet.',
        aiRewrite: 'Rewrite with AI',
        aiOverwriteTitle: 'Replace what you have written?',
        aiOverwriteBody: 'The AI draft replaces the current message, footer and buttons. You can undo once afterwards.',
        aiOverwriteConfirm: 'Replace',
        aiDraftBadge: 'AI draft — please review',
        aiDraftUndo: 'Undo',
        metaNamePreview: 'Meta name: {name}',
        metaNameLocked: 'Cannot be changed after submission.',
        metaNameFallback: 'Meta names allow English letters only, so this becomes “{name}”. Add a few English words to keep it recognisable.',
        headerLabel: 'Header (optional)',
        headerHint: 'One short line of bold text above the message. Variables are not supported here.',
        headerNone: 'None',
        headerText: 'Text',
        headerPlaceholder: 'e.g. Your booking is confirmed',
        buttonsHintV2: 'Either up to 3 quick replies, or 1 link + 1 call button. WhatsApp does not allow mixing the two.',
        buttonTypeQuickReply: 'Quick reply',
        buttonTypeUrl: 'Link',
        buttonTypePhone: 'Call',
        buttonLabelPlaceholder: 'Button label',
        buttonUrlPlaceholder: 'https://example.com/book',
        buttonPhonePlaceholder: '+85298765432',
        removeButton: 'Remove button',
        variableGroupCustomer: 'Customer',
        variableGroupCommerce: 'Order',
        variableGroupBooking: 'Booking',
        variableGroupOther: 'Other',
        customVariableTitle: 'Custom variable',
        customVariableLabel: 'Variable name',
        customVariableInvalid: 'Use lowercase letters, digits and underscores, starting with a letter — e.g. coupon_code.',
        customVariableAdd: 'Insert',
        samplesInlineHint:
          'Meta reviewers see these sample values, and the preview uses them. Leave a field blank to send the standard example — real values are filled in per customer.',
        checklistPassed: '{n} checks passed',
        checklistErrors: '{n} to fix',
        checklistWarnings: '{n} suggestion(s)',
        checklistAllGood: 'All checks passed — ready to submit.',
        checklistFix: 'Fix',
        submitBlocked: '{n} thing(s) still to fix',
        previewShowSamples: 'Sample values',
        previewShowVariables: 'Variable names',
        previewBillingMarketing: 'Marketing — billed per message sent.',
        previewBillingUtility: 'Utility — free inside a 24-hour customer session.',
        previewCompareSource: 'Original ({language})',
        rejectionTitle: 'Meta rejected this template',
        rejectionReasonLabel: 'Reason from Meta',
        rejectionFixTitle: 'What usually fixes it',
        pendingTitle: 'In review by Meta',
        backToList: 'Back to templates',
        draftRestoreTitle: 'Restore your unsaved draft?',
        draftRestoreBody: 'You left this editor with unsubmitted changes. Restore them, or start fresh.',
        draftRestore: 'Restore draft',
        draftDiscard: 'Start fresh',
        draftSaved: 'Draft saved locally',
        leaveTitle: 'Leave without submitting?',
        leaveBody: 'This template has not been submitted to Meta. Your draft is kept on this device.',
        leaveStay: 'Keep editing',
        leaveConfirm: 'Leave',
        lint: {
          bodyEmpty: 'Message body is empty.',
          bodyTooLong: 'Body exceeds 1,024 characters.',
          startsWithVariable: 'Meta rejects templates that start with a variable — add text before it.',
          endsWithVariable: 'Meta rejects templates that end with a variable — add text after it.',
          adjacentVariables: 'Two variables cannot sit next to each other — add text between them.',
          variableDensity: 'Very variable-heavy messages are often rejected — add more fixed text.',
          headerTooLong: 'Header exceeds 60 characters.',
          headerHasVariable: 'Headers must be fixed text — move the variable into the message body.',
          footerTooLong: 'Footer exceeds 60 characters.',
          buttonTooLong: 'Button labels are limited to 25 characters.',
          buttonTextEmpty: 'Every button needs a label.',
          tooManyButtons: 'Maximum 3 quick reply buttons.',
          tooManyCtaButtons: 'At most one link button and one call button.',
          mixedButtonTypes: 'Quick replies cannot be combined with link / call buttons — pick one style.',
          duplicateButtonText: 'Two buttons share the same label — Meta requires distinct labels.',
          buttonUrlInvalid: 'Enter a full link starting with https://',
          buttonPhoneInvalid: 'Enter the phone number with its country code, e.g. +85298765432.',
          missingSamples: 'Provide a sample value for every variable.',
          marketingNoOptOut: 'Marketing templates approve faster with an opt-out hint (e.g. "Reply STOP to unsubscribe").',
          categoryMismatch: 'This reads like a promotion but is filed as Utility — Meta often rejects that. Switch to Marketing or soften the wording.',
        },
        rejection: {
          invalidFormat: 'The template format is invalid — usually a variable at the start/end, adjacent variables, or a parameter/sample mismatch.',
          tagContentMismatch: 'The content does not match the chosen category — e.g. promotional wording in a UTILITY template. Change the category or the wording.',
          abusiveContent: 'Meta flagged the content as violating its policies. Review the wording.',
          scam: 'Meta suspected fraudulent content. Remove urgency/pressure wording and unclear offers.',
          generic: 'Check the wording against WhatsApp template policies, edit, and resubmit.',
        },
        presetVars: {
          customer_name: 'Customer name',
          business_name: 'Business name',
          order_number: 'Order number',
          service_name: 'Service name',
          product_name: 'Product name',
          date: 'Date',
          time: 'Time',
          amount: 'Amount',
          reference: 'Reference code',
          review_url: 'Review link',
          payment_instructions: 'Payment details',
          reason: 'Reason',
        },
      },
    },
    payment: {
      onlinePlatforms: 'Online payment platforms',
      onlinePlatformsDesc: 'Once connected, payment links are auto-generated for orders placed through your storefront or via AI chat',
      stripeDesc: 'Credit card, Apple Pay, Google Pay',
      stripePayments: 'Supports credit card, Apple Pay, Google Pay, and more',
      airwallexDesc: 'Multi-currency payments, ideal for cross-border merchants',
      manualMethods: 'Manual payment methods',
      manualMethodsDesc: 'Customers pay offline using these methods. Once you confirm the order, payment instructions are sent automatically.',
      methodInstructionsPlaceholder: 'Payment instructions for customers (e.g. account number, transfer details)',
      addMethod: 'Add payment method',
      manualHint: 'You will need to manually verify payment and update the order status.',
      vacationMode: 'Vacation mode',
      vacationModeDesc: 'When enabled, the AI will not accept new orders or bookings',
      returnDate: 'Expected return date',
      vacationMessage: 'Message for customers',
      vacationMessagePlaceholder: 'e.g. We will be back on Monday!',
      flowTitle: 'Payment flow',
      flowDesc: 'How payments are handled when an order is placed',
      flowStep1: 'After the AI places an order, if an online payment platform is connected, a payment link is automatically sent to the customer.',
      flowStep2: 'Once the customer pays, the order is automatically marked as paid and you are notified.',
      flowStep3: 'If no online platform is connected, customers see the manual methods you configured and follow your instructions to pay. You confirm receipt and update the order yourself.',
      stripeConnected: 'Stripe connected',
      stripeSetupIncomplete: 'Stripe setup incomplete',
      stripeEnabled: 'Enabled',
      stripeSetup: 'Setting up',
      stripeVerified: '✓ Account verified',
      stripeVerifying: '⚠ Account verification in progress',
      stripeChargesEnabled: '✓ Payments enabled',
      stripeChargesDisabled: '⚠ Payments not yet enabled',
      stripePayoutsEnabled: '✓ Automatic payouts enabled',
      stripePayoutsHint: 'Manage payouts in account settings',
      stripeContinue: 'Continue setup',
      stripeDisconnect: 'Disconnect',
      stripeDisconnecting: 'Disconnecting...',
      stripeDisconnectConfirm: 'Are you sure you want to disconnect Stripe? Customers will no longer be able to pay via online payment links.',
      stripeConnect: 'Connect',
      stripeConnecting: 'Connecting...',
      stripeDashboard: 'Manage Stripe',
      stripeCollapse: 'Collapse panel',
      stripeLoadingPanel: 'Loading Stripe panel...',
      stripeLoadError: 'Failed to load Stripe panel',
      stripeMissingKey: 'Stripe publishable key not configured',
      stripeTabs: { payments: 'Transactions', payouts: 'Payouts', balances: 'Balances', account: 'Account settings' },
    },
    team: {
      title: 'Team',
      desc: 'Manage team members and permissions',
      inviteTitle: 'Invite member',
      invitePlaceholder: 'Email address',
      invite: 'Invite',
      inviting: 'Inviting...',
      pendingInvites: 'Pending invitations',
      expires: 'Expires:',
      copyLink: 'Copy link',
      copied: 'Copied',
      removeConfirm: 'Are you sure you want to remove this member?',
      revokeConfirm: 'Are you sure you want to revoke this invitation?',
      you: 'You',
    },
    notifications: {
      inviteLinkHint: 'Send this link to the person. They will automatically join your team after signing in.',
      inviteLinkExpiry: 'An invite link will be generated after inviting. You can {copyLink} it to the person. Invitations expire in 7 days.',
    },
    whatsapp: {
      title: 'WhatsApp Connection',
      desc: 'Connect your WhatsApp Business account so the AI can automatically reply to customers',
      connected: 'WhatsApp connected',
      phoneNumber: 'Phone number:',
      displayName: 'Display name:',
      linkedAt: 'Connected:',
      features: 'AI features:',
      featuresItems: {
        autoReply: 'Auto-reply to customer WhatsApp messages',
        productOrders: 'Handle product inquiries and orders',
        bookingManagement: 'Booking management',
        mediaUnderstanding: 'Image and voice message understanding',
      },
      disconnect: 'Disconnect WhatsApp',
      disconnecting: 'Disconnecting...',
      disconnectConfirm: 'Are you sure you want to disconnect WhatsApp? The AI will no longer be able to send or receive WhatsApp messages.',
      connectTitle: 'Connect WhatsApp Business',
      steps: {
        step1: 'Step 1',
        step1Desc: 'Click the button below to start',
        step2: 'Step 2',
        step2Desc: 'Sign in to your Facebook account and select your WhatsApp Business account',
        step3: 'Step 3',
        step3Desc: 'Once confirmed, the AI will start working automatically',
      },
      connectButton: 'Connect WhatsApp',
      fbSuccess: 'Facebook authorisation successful!',
      fbSuccessDesc: 'Please enter your WhatsApp Business Account ID and Phone Number ID to complete setup. You can find these at developers.facebook.com > Your App > WhatsApp > API Setup.',
      wabaId: 'WhatsApp Business Account ID',
      phoneNumberId: 'Phone Number ID',
      verifying: 'Verifying...',
      completeSetup: 'Complete setup',
      metaNotConfigured: 'Meta App ID not configured. Please contact the administrator.',
      fbNotLoaded: 'Facebook SDK not loaded. Please refresh the page.',
      setupFailed: 'Setup failed. Please try again.',
      errors: {
        TOKEN_EXCHANGE_FAILED: 'Facebook authorisation could not be completed. Please try connecting again.',
        SIGNUP_FAILED: 'WhatsApp connection failed. Please try again.',
        NO_PENDING_TOKEN: 'No Facebook authorisation found. Please complete the "Connect WhatsApp" step first.',
        WABA_VERIFY_FAILED: 'Could not verify the WABA ID. Check that the ID is correct and that you have completed Facebook authorisation.',
        PHONE_NOT_IN_WABA: 'This Phone Number ID does not belong to this WABA. Available numbers: {availablePhones}',
        NUMBER_IN_USE: 'This number is connected to another NovixOne account. Disconnect it there first, or contact support.',
        WABA_NO_PHONES: 'No phone numbers found under this WABA. Add a phone number in Meta Business Suite first.',
      },
    },
    billing: {
      title: 'Plans & Billing',
      desc: 'Manage your subscription',
      loading: 'Loading...',
      loadFailed: 'Failed to load',
      currentPlan: 'Current Plan',
      manage: 'Manage',
      used: 'used',
      resets: 'Resets:',
      choosePlan: 'Choose a Plan',
      popular: 'Popular',
      current: 'Current',
      currentPlan2: 'Current Plan',
      perMonth: 'mo',
      processing: 'Processing...',
      upgrade: 'Upgrade',
      downgrade: 'Downgrade',
      enterpriseDesc: 'Unlimited AI credits, dedicated account manager, custom integrations',
      contactUs: 'Contact Us',
      subscriptionSuccess: 'Subscription successful! Your plan has been updated.',
      credits: {
        title: 'AI Credits',
        desc: 'One balance for every AI feature — replies, Copilot, knowledge and insights all draw from it.',
        totalRemaining: 'Credits remaining',
        monthlyGrant: 'Monthly plan credits',
        purchasedBalance: 'Purchased credits (never expire)',
        replyAnchor: '1 AI reply ≈ {n} credits',
        burnTitle: 'Usage — last {days} days',
        burnEmpty: 'No AI usage in this period yet.',
        burnSummary: 'Used in the last {days} days: {count} credits',
        unit: 'credits',
        groupLabels: {
          customer_ai: 'Customer AI',
          copilot: 'Copilot',
          knowledge: 'Knowledge',
          insights: 'Insights',
          drafts: 'Drafts',
          embeddings: 'Search',
          other: 'Other',
        },
        packsTitle: 'Buy Credits',
        packsDesc: 'One-time purchase. Purchased credits never expire.',
        packCredits: '{n} credits',
        packBuy: 'Buy',
        purchaseSuccess: 'Credits purchased! They have been added to your balance.',
        // Usage & limits (mobile parity with /usage — RFC usage-revamp Phase 5)
        forecastUnlimited: 'Unlimited plan — nothing to forecast.',
        forecastInsufficient: 'Fewer than 3 days of data — no forecast yet.',
        forecastOk: 'At this pace you will end the period at about {pct}% of your plan.',
        forecastTight: 'At this pace you will end the period at {pct}% of your plan — worth watching.',
        forecastRunout: 'At this pace all credits run out on {date} ({days} days from now).',
        forecastPace: 'Averaging {n} credits/day over the last 7 days',
        daysLeft: '{n} days left in period',
        macLabel: 'Active customers this month',
        macNoSnapshot: 'Shown after the first nightly count',
        limitsTitle: 'Plan limits',
        limitsSubtitle: 'The same numbers the paywalls enforce',
        limitsShow: 'Show limits',
        limitsHide: 'Hide',
        limitsUnlimited: 'Unlimited',
        limitsNotMeasured: 'Not measured',
        limitsNotEnforced: 'Not enforced',
        limitsAtCap: 'At limit',
        limitLabels: {
          monthlyActiveCustomers: 'Active customers this month',
          maxTeamMembers: 'Team members',
          maxWhatsappNumbers: 'WhatsApp numbers',
          maxProducts: 'Products / services',
          maxKnowledgeEntries: 'Knowledge entries',
          maxCustomerSegments: 'Customer segments',
          maxAutomationRules: 'Automation rules',
          maxLocations: 'Locations',
          maxCouponCampaigns: 'Coupon campaigns',
          maxCustomFieldsPerObject: 'Custom fields (per object)',
          webChatMaxOrigins: 'Web chat origins',
          monthlyWaTemplateBroadcasts: 'WhatsApp broadcasts this month',
          monthlyEmailBroadcasts: 'Email broadcasts this month',
          monthlyAutomationExecutions: 'Automation runs this month',
          maxCouponCodesPerBatch: 'Coupon codes per batch',
          mediaStorageMb: 'Media storage',
          mediaRetentionDays: 'Media retention',
          auditRetentionDays: 'Audit log retention',
        },
      },
      planBullets: {
        free: [
          '50K AI credits / month',
          '1 WhatsApp number',
          'Up to 2 team members',
          'Unlimited products · 50 knowledge entries (import from website / PDF)',
          'Basic customer CRM + order management',
          'Mobile app + 0% Stripe Connect fee',
        ],
        starter: [
          '250K AI credits / month',
          '1 WhatsApp number · 5 team members',
          'Unlimited products · 500 knowledge entries',
          'WhatsApp broadcasts to 500 recipients / month',
          'Email broadcasts to 500 recipients / month',
          'Everything in Free',
        ],
        pro: [
          '1M AI credits / month',
          '3 WhatsApp numbers · 15 team members',
          'Up to 3 locations · 5,000 knowledge entries',
          'Cohort & funnel analytics',
          'WhatsApp broadcasts to 5,000 recipients / month',
          'Everything in Starter',
        ],
        business: [
          '4M AI credits / month',
          '10 WhatsApp numbers · 50 team members',
          'Up to 10 locations · unlimited knowledge',
          'Custom roles + granular permissions · custom domain',
          '365-day audit log',
          'Everything in Pro',
        ],
        enterprise: [
          'Unlimited AI credits · unlimited team',
          'Limits and audit retention set by contract',
          'Data processing agreement (DPA)',
          'Dedicated account manager + SLA',
          'Dedicated deployment (negotiable)',
        ],
      },
    },
  },

  mobile: {
    tabs: {
      home: 'Home',
      conversations: 'Chats',
      customers: 'Customers',
      business: 'Business',
      more: 'Settings',
    },
    datetime: {
      justNow: 'Just now',
      today: 'Today',
      tomorrow: 'Tomorrow',
      yesterday: 'Yesterday',
    },
    settings: {
      closeAccount: {
        title: 'Close account',
        intro: 'Closing deactivates the shop at once and permanently deletes every customer, conversation, order and booking after the grace period. Audit records are kept for compliance.',
        graceHint: '{days}-day grace period — you can cancel any time before it ends.',
        confirmLabel: 'Type the shop name ({name}) to confirm',
        confirmPlaceholder: 'Shop name',
        button: 'Close account',
        working: 'Closing…',
        failed: 'Could not start the closure',
        nameMismatch: 'The name does not match.',
        confirmAlertTitle: 'Close this account?',
        confirmAlertBody: 'The shop stops working for every teammate right away. You can cancel within the grace period.',
        confirmAlertCancel: 'Keep account',
        confirmAlertConfirm: 'Close account',
        pendingTitle: 'Closure scheduled — data will be deleted on {date}',
        pendingBody: 'The shop is deactivated. Cancel to reactivate it and keep everything.',
        cancelButton: 'Cancel closure',
        cancelling: 'Cancelling…',
        cancelFailed: 'Could not cancel',
        ownerOnly: 'Only the account owner can close the account.',
      },
      region: {
        title: 'Region',
        save: 'Save',
        saveError: 'Save failed',
        unknownError: 'Unknown error',
        introHeading: 'Where do you mainly do business?',
        introBody: 'Controls the default phone dial code, payment method suggestions, and future region-specific features. This does not affect timezone or language.',
        tapToChange: 'Tap to change',
        unsavedNotice: 'Unsaved: changing from "{from}" to "{to}"',
      },
      profile: {
        title: 'Profile',
        save: 'Save',
        saveError: 'Save failed',
        unknownError: 'Unknown error',
        introHeading: 'Just your account',
        introBody: 'Changes on this page only affect your personal sign-in experience — not the business or your teammates.',
        displayNameLabel: 'Display name',
        displayNamePlaceholder: 'e.g. Alex Chen',
        displayNameHint: 'Shown in notifications, system records, and the team screen.',
        emailLabel: 'Sign-in email',
        emailSignedOut: 'Not signed in',
        emailHint: 'Your email is your sign-in identity and can\'t be changed from the app right now.',
      },
      timezone: {
        title: 'Timezone',
        save: 'Save',
        saveError: 'Save failed',
        unknownError: 'Unknown error',
        introHeading: 'Pick the timezone you do business in',
        introBody: 'Controls the "today" / "this week" boundary, booking reminders, and automated follow-up scheduling.',
        currentSelection: 'Current selection',
        searchPlaceholder: 'Search city or UTC offset',
        emptyResults: 'No results',
        regions: {
          asia: 'Asia',
          pacific: 'Pacific',
          europe: 'Europe',
          americas: 'Americas',
          africa: 'Africa',
          middleEast: 'Middle East',
        },
      },
      account: {
        title: 'Account',
        save: 'Save',
        saveError: 'Save failed',
        unknownError: 'Unknown error',
        phoneInvalidTitle: 'Invalid phone number',
        phoneInvalidBody: 'Please check the phone number.',
        introHeading: 'Your business identity',
        introBody: 'This information shows up on receipts, AI replies, and anywhere customers see your brand. The clearer you write it, the more your AI speaks in your voice.',
        nameLabel: 'Business name',
        namePlaceholder: 'e.g. Clearpeak Aircon Engineering',
        nameHint: 'Shown on receipts and when AI introduces itself.',
        typeLabel: 'Business type',
        typePlaceholder: 'e.g. physiotherapy centre, homeware retail, interior design',
        descLabel: 'Business description',
        descLabelHint: 'A sentence or two about what you do',
        descPlaceholder: 'e.g. Homeware retailer with shops in Causeway Bay and Tsuen Wan, also taking orders online and on WhatsApp.',
        phoneLabel: 'Contact phone',
        phoneHint: "Your personal phone — AI will reach you here when escalating a customer. Different from the customer-facing WhatsApp number.",
        nameRequired: 'Business name cannot be empty.',
      },
      notifications: {
        title: 'Notifications',
        loadError: "Couldn't load notification preferences",
        introHeading: 'Pick how you want to be notified',
        introBody: 'These are your personal preferences — they only affect how you receive notifications, not teammates or the business itself.',
        footnote: 'Email notifications may be delayed by a few minutes.',
        events: {
          escalation: {
            title: 'Needs handoff',
            description: "When AI can't handle it or a customer insists on a human, you'll know immediately.",
          },
          newOrder: {
            title: 'New order',
            description: 'Triggered when a new order comes in (whether through your storefront, AI chat, or the customer portal).',
          },
          appointment: {
            title: 'Appointment reminders',
            description: 'New bookings, reschedules, and 24h / 2h advance reminders.',
          },
          inquiry: {
            title: 'Customer inquiry',
            description: 'Complaints, questions, or new inquiries land here.',
          },
          followUp: {
            title: 'Follow-up due',
            description: 'When a follow-up you scheduled comes due, or one goes overdue.',
          },
          inventory: {
            title: 'Low stock',
            description: 'A daily digest of items that have fallen to or below their low-stock line.',
          },
          integrationApproval: {
            title: 'Approval needed',
            description: 'The AI wants to do something in your own system that needs a colleague to approve. Sent only to people who can approve it, even during quiet hours.',
          },
          channel: {
            title: 'WhatsApp connection',
            description: 'When Meta disconnects one of your WhatsApp numbers, or it comes back. While a number is down, the AI cannot answer customers on it. Sent even during quiet hours.',
          },
          dailyReport: {
            title: 'Daily summary',
            description: 'A short end-of-day summary at 8pm local time.',
          },
        },
        channels: {
          webPush: 'Push',
          email: 'Email',
        },
      },
      hours: {
        title: 'Business hours',
        save: 'Save',
        saveError: 'Save failed',
        unknownError: 'Unknown error',
        loadError: 'Failed to load',
        timeFormatTitle: 'Invalid time format',
        timeFormatBody: '{day} needs a HH:MM time (e.g. 09:00)',
        timeOrderTitle: 'Invalid time range',
        timeOrderBody: "{day}'s closing time must be later than opening time",
        introHeading: 'Set your daily business hours',
        introBody: 'These hours apply to your storefront, bookings, and AI replies — e.g. off-hours auto-replies and last-order cut-off reminders.',
        days: {
          monday: { label: 'Monday', short: 'Mon' },
          tuesday: { label: 'Tuesday', short: 'Tue' },
          wednesday: { label: 'Wednesday', short: 'Wed' },
          thursday: { label: 'Thursday', short: 'Thu' },
          friday: { label: 'Friday', short: 'Fri' },
          saturday: { label: 'Saturday', short: 'Sat' },
          sunday: { label: 'Sunday', short: 'Sun' },
        },
        closed: 'Closed',
        toSeparator: 'to',
        timezoneNote: 'Timezone: {tz}',
      },
      categories: {
        title: 'Product categories',
        introHeading: 'Product categories',
        introBody: 'Group your products and services with categories so you can manage and assign staff more easily.',
        emptyTitle: 'No categories yet',
        emptySub: 'Add categories to organise your catalog, e.g. "Nails", "Skincare", "Massage".',
        deactivated: 'Deactivated',
        failed: 'Failed',
        sheetTitleCreate: 'New category',
        sheetTitleEdit: 'Edit category',
        nameLabel: 'Name',
        namePlaceholder: 'e.g. Nails / Massage / Skincare',
        descLabel: 'Description',
        descPlaceholder: 'Optional — short description of this category',
        saveCreate: 'Create category',
        saveEdit: 'Save changes',
        deleteBtn: 'Delete category',
        deleteConfirmTitle: 'Delete category?',
        deleteConfirmBody: 'Delete "{name}" — the products inside will NOT be deleted; they just won\'t have a category anymore.',
        deleteConfirmCancel: 'Cancel',
        deleteConfirmAction: 'Delete',
      },
      outreach: {
        title: 'Automated outreach',
        loadError: "Couldn't load outreach settings",
        introHeading: 'Let AI reach out to customers for you',
        introBody: "Once enabled, AI sends WhatsApp follow-up messages at the right moments — you don't have to remember who to follow up with.",
        footnote: "WhatsApp requires approved Meta templates for automated outreach. Turning on a toggle here is necessary but not sufficient — you'll also need to confirm the template is set up in the web dashboard's outreach section.",
        triggers: {
          abandonedInterest: {
            title: 'Asked but didn\'t order',
            tagline: 'Gentle next-day nudge',
            description: 'Customer asked about a product or service but never placed an order — AI follows up the next day using your approved template to reduce drop-off.',
            cadence: 'Within 24 hours · max 1 per customer every 7 days',
          },
          lapsedCustomer: {
            title: 'Quiet regular',
            tagline: 'Proactive win-back',
            description: "Customers who bought before but have been silent for 14 days — AI remembers them for you and sends a warm hello message to bring them back for another order.",
            cadence: 'After 14 days quiet · max 1 per customer every 30 days',
          },
          postOrder: {
            title: 'Post-order check-in',
            tagline: 'Thoughtful follow-through',
            description: '72 hours after an order or appointment is completed, AI checks in to confirm everything went well — perfect moment to collect a review and build loyalty.',
            cadence: '72 hours after completion · max 1 per customer every 14 days',
          },
        },
      },
      policies: {
        title: 'Policies',
        save: 'Save',
        saveError: 'Save failed',
        saveSuccessTitle: 'Saved',
        saveSuccessBody: 'Policies updated.',
        unknownError: 'Unknown error',
        sectionBooking: 'Booking cancellation policy',
        sectionOrder: 'Order cancellation policy',
        sectionVacation: 'Vacation mode',
        allowCancelBooking: 'Allow customers to cancel bookings',
        allowCancelOrder: 'Allow customers to cancel orders',
        windowLabel: 'Cancellation window',
        windowBookingOption: '{hours}h before',
        windowOrderOption: 'within {hours}h',
        windowBookingHint: 'How many hours before the booking the customer can still cancel and get a refund',
        windowOrderHint: 'How many hours after ordering a customer can still cancel',
        refundLabel: 'Refund policy',
        refundFull: 'Full refund',
        refundPartial: 'Partial refund',
        refundNone: 'No refund',
        refundPercentLabel: 'Refund percent',
        blockIfShipped: "Can't cancel after shipped",
        vacationToggle: 'Enable vacation mode',
        vacationHint: "When enabled, AI stops handling new orders and bookings and only replies with the message you set.",
        vacationReturnDate: 'Return date',
        vacationMessage: 'Vacation message',
        vacationMessagePlaceholder: "e.g. We're on holiday, back Jan 3 — see you then!",
        notConfigured: 'Cancellation policy not configured',
        notConfiguredHint: 'AI will escalate every cancellation request to you. Configure a policy to let AI handle them automatically.',
        configureButton: 'Configure policy',
        clearButton: 'Clear policy',
      },
      soul: {
        title: 'AI personality',
        save: 'Save',
        saveError: 'Save failed',
        unknownError: 'Unknown error',
        loadError: 'Failed to load',
        introHeading: 'Teach AI to sound like you',
        introBody: 'Pick a tone, add guidelines the AI should always follow, and red lines it must never cross. AI uses them on every reply.',
        sectionTone: 'Tone',
        sectionGuidelines: 'Guidelines',
        sectionRedLines: 'Red lines',
        guidelinesHint: 'Things AI should always do, e.g. "Ask the customer\'s name first" or "Quote prices including shipping".',
        redLinesHint: 'Things AI must never do, e.g. "Don\'t give ad-hoc discounts" or "Don\'t answer medical questions".',
        guidelinePlaceholder: 'Add a guideline',
        redLinePlaceholder: 'Add a red line',
        guidelinesEmptyTitle: 'No guidelines yet',
        guidelinesEmptySub: 'Add one so AI knows how to greet customers',
        redLinesEmptyTitle: 'No red lines yet',
        redLinesEmptySub: 'Add one to protect your brand',
        tones: {
          friendly: { label: 'Friendly', sub: 'Warm and approachable' },
          professional: { label: 'Professional', sub: 'Formal and polite' },
          casual: { label: 'Casual', sub: 'Chatty, like a friend' },
          enthusiastic: { label: 'Enthusiastic', sub: 'Lively and energetic' },
        },
      },
      availability: {
        title: 'Availability',
        saveSettings: 'Save settings',
        saveSchedule: 'Save schedule',
        saveError: 'Save failed',
        unknownError: 'Unknown error',
        settingsSavedTitle: 'Saved',
        settingsSavedBody: 'Booking settings updated.',
        scheduleSavedTitle: 'Saved',
        scheduleSavedBody: 'Weekly schedule updated — the new slots apply to storefront bookings, AI bookings, and manual entries.',
        blockFailedTitle: 'Failed to add',
        sectionSettings: 'Booking settings',
        sectionWeekly: 'Weekly schedule',
        sectionBlocked: 'Blocked dates',
        staffModeLabel: 'Staff assignment',
        staffModes: {
          none: { label: 'Not needed', sub: 'Use global capacity, no staff assignment' },
          auto: { label: 'Auto assign', sub: 'System picks a staff member automatically' },
          choice: { label: 'Customer picks', sub: 'AI asks the customer which staff they want' },
        },
        bufferLabel: 'Buffer minutes',
        bufferHint: 'Buffer time between each booking',
        maxDaysLabel: 'Max booking window (days)',
        maxDaysHint: 'How far in advance customers can book',
        weeklyHint: "AI will only book customers inside these time windows. \"Capacity\" is how many customers can be served at the same time.",
        capacityLabel: 'Capacity',
        capacitySuffix: 'customers',
        addRange: 'Add range',
        blockedHint: "Holidays or special closures — AI won't accept bookings on these days.",
        blockDatePlaceholder: 'Date (YYYY-MM-DD)',
        blockReasonPlaceholder: 'Reason (optional)',
        dayLabels: {
          sun: 'Sun',
          mon: 'Mon',
          tue: 'Tue',
          wed: 'Wed',
          thu: 'Thu',
          fri: 'Fri',
          sat: 'Sat',
        },
      },
      payment: {
        title: 'Payment methods',
        save: 'Save',
        saveError: 'Save failed',
        unknownError: 'Unknown error',
        loadError: 'Failed to load',
        deleteConfirmTitle: 'Delete this payment method?',
        deleteConfirmBody: "This can't be undone.",
        deleteConfirmCancel: 'Cancel',
        deleteConfirmAction: 'Delete',
        nameEmptyTitle: 'Name required',
        nameEmptyBody: 'Payment method #{index} needs a name, e.g. "Bank transfer"',
        introHeading: 'Tell customers how to pay',
        introBody: 'Add payment methods like bank transfer, PayPal, Wise. Customers see them when checking out via your storefront or placing orders through AI.',
        emptyTitle: 'No payment methods yet',
        emptyHint: 'Tap below to add your first',
        addBtn: 'Add payment method',
        cardIndexLabel: '#{index}',
        statusActive: 'Active',
        statusInactive: 'Inactive',
        namePlaceholder: 'Name, e.g. Bank transfer',
        instructionsPlaceholder: 'Details, e.g. HSBC 123-456789 / Account name: ABC Studio',
      },
      stripeConnect: {
        title: 'Accept card payments',
        introHeading: 'Let customers pay with cards',
        introBody: 'Connect Stripe so AI can send a secure payment link when closing an order — credit card, Apple Pay and Google Pay included.',
        statusNotConnected: 'Not connected',
        statusSetupIncomplete: 'Setup incomplete',
        statusConnected: 'Connected',
        statusChargesDisabled: 'Charges not enabled',
        statusPayoutsDisabled: 'Payouts pending review',
        badgeVerified: 'Verified',
        badgePending: 'Pending',
        connectBtn: 'Set up Stripe',
        continueBtn: 'Complete setup',
        manageBtn: 'Update Stripe details',
        disconnectBtn: 'Disconnect Stripe',
        disconnectConfirmTitle: 'Disconnect Stripe?',
        disconnectConfirmBody: "Customers won't be able to pay by card until you reconnect.",
        disconnectConfirmCancel: 'Cancel',
        disconnectConfirmAction: 'Disconnect',
        disconnectError: "Couldn't disconnect Stripe",
        loadError: "Couldn't load Stripe status",
        adminOnly: 'Only the shop admin can set up Stripe.',
        sessionMissing: 'Please sign out and back in to refresh your session, then try again.',
        feesNote: 'Stripe charges its own processing fees per transaction. NovixOne does not add any markup.',
        webTitle: 'Stripe setup',
        webClose: 'Close',
        webLoading: 'Loading Stripe…',
        webError: 'Could not load Stripe. Check your connection and try again.',
        webRetry: 'Retry',
      },
      billing: {
        title: 'Plan & usage',
        loadError: "Couldn't load billing info",
        currentPlanLabel: 'Your current plan',
        usageLabel: 'AI replies this month',
        unlimited: 'Unlimited',
        enterpriseHint: 'Your plan has no usage cap. Contact support if you have any issues.',
        remainingLabel: '{count} replies left',
        dangerWarning: "You're almost out of quota this month — once it's gone AI will stop replying automatically. Consider upgrading or buying a reply top-up.",
        warnWarning: 'More than 80% used — watch usage for the rest of the month.',
        topupHeading: 'Reply top-up',
        topupWithBalance: 'You have {count} top-up replies left. They kick in automatically after your monthly quota runs out.',
        topupWithoutBalance: 'No top-ups purchased. Top-up replies carry over between months and kick in after your monthly quota runs out. Purchase on the desktop dashboard.',
        upgradeBtn: 'Upgrade plan',
        manageBtn: 'Manage subscription',
        portalHint: 'Subscription management, payment methods, invoices, and cancellation are all handled through Stripe\'s secure pages.',
        openBrowserFailedTitle: "Couldn't open browser",
        openBrowserFailedBody: 'Please try again later.',
        plans: {
          free: { label: 'Free', sub: 'Basic trial — enough to get started' },
          starter: { label: 'Starter', sub: 'Solo studios' },
          pro: { label: 'Pro', sub: 'Steady business volume' },
          business: { label: 'Business', sub: 'High-volume, multi-customer' },
          enterprise: { label: 'Enterprise', sub: 'Custom usage' },
        },
      },
      apps: {
        title: 'Features',
        intro: 'Switch on what your shop does. A feature that is off disappears from menus, the AI and your shop page — nothing is deleted.',
        modulesHeading: 'Features',
        focusHeading: 'Focus',
        focusHint: 'What the AI leads with and your home screen shows first. It only changes presentation.',
        focusNone: 'Customer service first',
        focusNoneHint: 'The AI answers questions and does not push orders or bookings.',
        readOnly: 'Only owners and managers can change features.',
        lockedNote: 'Not in your current plan. Manage your plan at app.novixone.ai.',
        requiresOneOf: 'Needs {modules} on first.',
        requiresAll: 'Needs {modules} on first.',
        or: ' or ',
        notReadyOrders: 'On, but no product is listed yet — the AI cannot take orders.',
        notReadyBookings: 'On, but there is no bookable service yet — the AI cannot book.',
        confirmOffTitle: 'Turn off {module}?',
        confirmOffBody: '{impact} are kept and will run to completion. Customers and the AI can no longer create new ones.',
        impactOrders: '{count} open orders',
        impactBookings: '{count} upcoming bookings',
        impactDeals: '{count} open deals',
        confirmOffCancel: 'Cancel',
        confirmOffAction: 'Turn off',
        cascaded: 'Also turned off: {modules}',
        saveError: 'Could not save',
        loadError: 'Could not load features.',
        retry: 'Retry',
      },
      shop: {
        title: 'Shop settings',
        fallbackShop: 'Shop',
        businessTypeMissing: 'Business type not set',
        allShopHint: "Shop-wide · changes affect the whole business",
        pauseAlertTitle: 'Pause AI auto-replies?',
        pauseAlertBody: "New messages will wait for you to reply in person. AI quota isn't charged while paused.",
        pauseAlertCancel: 'Cancel',
        pauseAlertConfirm: 'Pause AI',
        aiPausedTitle: 'AI is paused',
        aiPausedHint: 'New messages will wait for you',
        aiActiveTitle: 'AI auto-reply',
        aiActiveHint: 'AI will handle conversations for you',
        planHeading: '{plan} plan',
        usageLoading: 'Loading…',
        usageLoaded: '{used} / {total} replies used',
        unlimited: 'Unlimited',
        usageWarnPercent: 'Usage at {percent}%',
        usageDangerPercent: 'Usage at {percent}% — almost gone',
        sectionBusiness: 'Business',
        sectionAI: 'AI assistant',
        sectionLocale: 'Region & language',
        rowHours: { title: 'Business hours', sub: 'Daily opening times' },
        rowPayment: { title: 'Payment methods', sub: 'Bank transfer, PayPal, Wise, etc.' },
        rowStripe: { title: 'Accept card payments', sub: 'Credit card, Apple Pay, Google Pay via Stripe' },
        rowAvailability: { title: 'Availability', sub: 'Weekly hours and blocked dates' },
        rowPolicies: { title: 'Policies', sub: 'Cancellation rules, refunds, vacation mode' },
        rowStaff: { title: 'Staff', sub: 'Team members and service assignment' },
        rowCategories: { title: 'Product categories', sub: 'Organise your catalog' },
        rowSoul: { title: 'AI personality', sub: 'Tone, guidelines, red lines' },
        rowOutreach: { title: 'Automated outreach', sub: 'Abandoned interest, win-back, post-order' },
        rowTemplates: { title: 'WhatsApp templates', sub: 'Approval status of message templates' },
        rowKnowledge: { title: 'Knowledge base', sub: 'FAQ, policies, stock answers' },
        rowAgents: { title: 'AI agents', sub: 'Personas, skills, triggers' },
        rowRegion: 'Region',
        rowTimezone: 'Timezone',
        groups: {
          account: 'Account',
          shop: 'Shop',
          operations: 'Operations',
          messaging: 'Messaging',
          advanced: 'Advanced',
          upgrade: 'Unlock with an upgrade',
        },
        rows: {
          account: { title: 'Shop account', sub: 'Name, business type, description' },
          business: { title: 'Shop profile', sub: 'Region, timezone, languages' },
          apps: { title: 'Features', sub: 'Orders, bookings, deals — on or off' },
          operations: { title: 'Policies', sub: 'Auto-confirm, cancellation, vacation mode' },
          scheduling: { title: 'Availability', sub: 'Weekly hours, blocked dates, rosters' },
          payment: { title: 'Payment methods', sub: 'Stripe, FPS, PayMe, bank transfer, cash' },
          templates: { title: 'Message templates', sub: 'Approval status of WhatsApp templates' },
          marketing: { title: 'Automated outreach', sub: 'Abandoned interest, win-back, post-order' },
          billing: { title: 'Plan & billing', sub: 'Plan, invoices, AI credits' },
          data: { title: 'Data & privacy', sub: 'Close this account' },
        },
        planSince: '{plan} and up',
        webOnlyHint: 'Team, channels, custom fields and integrations are managed on the web dashboard.',
        rowLanguage: 'AI reply language',
        languageLabels: {
          yue: '廣東話 (Cantonese)',
          zhHant: '繁體中文 (Traditional)',
          zhHans: '简体中文 (Simplified)',
          en: 'English',
        },
        planLabels: {
          free: 'Free',
          starter: 'Starter',
          pro: 'Pro',
          business: 'Business',
          enterprise: 'Enterprise',
        },
      },
    },
    languageScreen: {
      title: 'Language',
      saveError: 'Save failed',
      unknownError: 'Unknown error',
      heading: 'AI & dashboard language',
      hint: 'Controls the language your AI replies in and the language of this app. Independent of region — you can run a Hong Kong business in English if you serve international customers.',
      languageLabels: {
        zhHant: { label: '繁體中文', sub: 'Traditional Chinese' },
        zhHans: { label: '简体中文', sub: 'Simplified Chinese' },
        en: { label: 'English', sub: 'International default' },
      },
    },
    shared: {
      loadFailed: 'Failed to load',
      retry: 'Retry',
    },
    voice: {
      recognitionFailed: 'Speech recognition failed',
      permissionDenied: 'Microphone and speech recognition permission required',
    },
    more: {
      title: 'Settings',
      fallbackUser: 'User',
      fallbackShop: 'Shop',
      sectionPersonal: 'Personal',
      sectionShop: 'Shop',
      profile: 'Profile',
      profileSub: 'Display name, sign-in email',
      notifications: 'Notifications',
      notificationsSub: 'Choose which events notify you and where',
      language: 'App language',
      languageSub: 'Only affects this device. Does not change AI replies.',
      shopSettings: 'Shop settings',
      shopSettingsSub: '{shop} · AI, hours, billing…',
      shopSettingsHint: 'Shop settings affect the whole business and every teammate',
      sectionData: 'Data & privacy',
      closeAccount: 'Close account',
      closeAccountSub: 'Deactivate the shop and delete its data after a grace period',
      signOut: 'Sign out',
      support: 'Help & support',
      supportSub: 'Contact the NovixOne team, see your requests',
    },
    helpDesk: {
      title: 'Support',
      subtitle: 'Your requests to the NovixOne support team.',
      newRequest: 'New request',
      empty: 'No support requests yet',
      emptyHint: 'When you contact support, the conversation shows up here.',
      loadError: 'Could not load your support requests.',
      retry: 'Retry',
      category: 'What is it about?',
      categories: { billing: 'Billing & plan', whatsapp: 'WhatsApp', line: 'LINE', ai: 'AI replies', bookings: 'Bookings', orders: 'Orders & payments', account: 'Account & sign-in', other: 'Something else' },
      subject: 'Subject',
      subjectPlaceholder: 'e.g. Customers on WhatsApp get no reply',
      description: 'What happened?',
      descriptionPlaceholder: 'What did you do, what did you expect, and what happened instead?',
      contact: 'Best way to reach you (optional)',
      contactPlaceholder: 'e.g. WhatsApp +852 …, or e-mail',
      attachedNote: 'Sent with your request: the app version, your phone platform and your role — to help us find the problem.',
      send: 'Send request',
      sending: 'Sending…',
      sent: 'Request {ref} sent — we will reply here and by e-mail.',
      sendFailed: 'The request could not be sent. Please try again.',
      subjectTooShort: 'Give the subject at least 3 characters.',
      descriptionTooShort: 'Describe the problem in at least a few words.',
      statuses: { open: 'Open', pending_merchant: 'Waiting for you', pending_internal: 'With our engineers', resolved: 'Solved', closed: 'Closed' },
      waitingForYou: 'Support replied — waiting for you',
      updated: 'Updated',
      you: 'You',
      supportName: 'NovixOne support',
      replyPlaceholder: 'Write a reply…',
      replySend: 'Send',
      replyFailed: 'The reply could not be sent.',
      markSolved: 'Mark as solved',
      markSolvedConfirm: 'Mark this request as solved?',
      markSolvedBody: 'You can still reply within 7 days to reopen it.',
      cancel: 'Cancel',
      solvedDone: 'Marked as solved.',
      closedNote: 'This request is closed. If the problem is back, please open a new request.',
      resolvedNote: 'This request is solved. Reply within 7 days if something is still wrong.',
      csatQuestion: 'How did we do?',
      csatGood: 'Helpful',
      csatBad: 'Not helpful',
      csatThanks: 'Thank you for the feedback.',
      slaNote: 'Our team answers in business hours (Mon–Fri 09:00–18:00 HKT).',
      notFound: 'This support request was not found.',
      announcementSeverity: { info: 'Notice', warning: 'Important', critical: 'Urgent' },
      announcementKinds: { incident: 'Service issue', maintenance: 'Maintenance', feature: 'New', billing: 'Billing' },
      dismiss: 'Dismiss',
      learnMore: 'Details',
      update: 'Update',
    },
    couponCheck: {
      title: 'Check a coupon',
      subtitle: 'Verify a code a customer shows you — no hold is created.',
      inputPlaceholder: 'Enter code, e.g. SUMMER100',
      checkBtn: 'Check',
      checking: 'Checking…',
      validTitle: 'Valid ✓',
      invalidTitle: 'Not usable',
      campaignLabel: 'Campaign',
      discountLabel: 'Discount: {amount}',
      subtotalCaveat: 'Estimated on a {amount} order — the exact amount is computed at checkout.',
      minSpendLine: 'Min spend {amount}',
      expiresLine: 'Expires {date}',
      nextStepHint: 'To redeem: create the order with this code — the discount applies and the code is consumed there.',
      errorGeneric: 'Check failed — try again.',
    },
    business: {
      title: 'Business',
      pillarOrders: 'Orders',
      pillarBookings: 'Bookings',
      pillarInquiries: 'Inquiries',
      pillarProducts: 'Products',
    },
    businessSheets: {
      booking: {
        title: 'New booking',
        save: 'Save',
        unknownError: 'Unknown error',
        fieldCustomer: 'Customer',
        fieldCustomerPlaceholder: 'Pick a customer or add new',
        fieldService: 'Service',
        fieldServicePlaceholder: 'Pick a service',
        usePackage: 'Use package “{name}” ({left} left)',
        usePackageHint: 'Takes one session — nothing more to pay',
        serviceValue: '{duration} · {price}',
        fieldDate: 'Date',
        fieldTime: 'Time',
        fieldNotes: 'Notes',
        notesPlaceholder: 'Customer requests, special arrangements…',
        hourLabel: 'Hour',
        minuteLabel: 'Minute',
        pickServiceTitle: 'Pick a service',
        noServicesTitle: 'No services yet',
        noServicesHint: 'Go to Products and add one with type set to Service',
        serviceMinutes: '{minutes} min',
        errors: {
          noCustomerTitle: 'No customer',
          noCustomerBody: 'Please pick a customer first',
          noServiceTitle: 'No service',
          noServiceBody: 'Please pick a service first',
          timeTitle: 'Invalid time',
          timeBody: 'Please pick a valid date and time',
          createFailedTitle: "Couldn't create",
          slotTitle: 'This time is not available',
          slotBody: 'Book anyway?',
          bookAnyway: 'Book anyway',
          cancel: 'Cancel',
          resourceWarningTitle: 'Booked without a room',
          resourceWarningBody: 'No room / equipment was free for this booking — please assign one.',
        },
      },
      inquiry: {
        title: 'New inquiry',
        save: 'Save',
        unknownError: 'Unknown error',
        introTitle: 'Log an inquiry on the fly',
        introBody: 'Capture what customers mention on calls or walk-ins so nothing falls through the cracks.',
        fieldCustomer: 'Customer',
        fieldCustomerPlaceholder: 'Pick a customer or add new',
        fieldType: 'Type',
        fieldPriority: 'Priority',
        fieldSubject: 'Subject',
        fieldDescription: 'Details',
        subjectPlaceholder: 'One line about what happened',
        descriptionPlaceholder: 'What the customer said, what they want, expected follow-up…',
        types: {
          inquiry: { label: 'Inquiry', sub: 'General question' },
          complaint: { label: 'Complaint', sub: 'Needs follow-up' },
          feedback: { label: 'Feedback', sub: 'Suggestions / comments' },
          support: { label: 'Support', sub: 'Tech / product support' },
        },
        priorities: {
          low: 'Low',
          normal: 'Normal',
          high: 'High',
          urgent: 'Urgent',
        },
        errors: {
          noCustomerTitle: 'No customer',
          noCustomerBody: 'Please pick a customer first',
          noSubjectTitle: 'Subject required',
          noSubjectBody: 'Please add a subject for this inquiry',
          noDescriptionTitle: 'Details required',
          noDescriptionBody: 'Please add the details',
          createFailedTitle: "Couldn't create",
        },
      },
      product: {
        titleCreate: 'New item',
        titleEdit: 'Edit item',
        typeProduct: 'Product',
        typeService: 'Service',
        fieldName: 'Name',
        nameProductPlaceholder: 'e.g. handmade soap',
        nameServicePlaceholder: 'e.g. 60-minute massage',
        fieldPrice: 'Price ({currency})',
        pricePlaceholder: '0',
        fieldDuration: 'Duration (minutes)',
        durationPlaceholder: 'e.g. 60',
        fieldDescription: 'Description',
        descriptionPlaceholder: 'What customers see in chat',
        fieldCategory: 'Category',
        noCategory: 'No category',
        availableOn: 'Listed',
        availableOnSub: 'Customers see this in chat',
        availableOff: 'Unlisted',
        availableOffSub: 'Hidden from customers for now',
        advanced: 'Advanced settings',
        fieldOrderingMode: 'Ordering mode',
        orderingDirect: 'Direct order',
        orderingInquiry: 'Quote / inquiry',
        fieldPriceRange: 'Price range (optional)',
        priceMin: 'Min',
        priceMax: 'Max',
        fieldQuotationNotes: 'Quote notes',
        quotationPlaceholder: 'e.g. depends on style and size',
        fieldPaymentTiming: 'Payment timing',
        paymentFullUpfront: 'Full upfront',
        paymentDeposit: 'Deposit',
        paymentOnCompletion: 'On completion',
        fieldDepositPercent: 'Deposit percent (%)',
        depositPlaceholder: 'e.g. 30',
        fieldFulfillment: 'Fulfillment',
        fulfillmentDelivery: 'Delivery',
        fulfillmentPickup: 'Pickup',
        fulfillmentBoth: 'Delivery or pickup',
        fulfillmentNone: 'N/A',
        fieldServiceDelivery: 'Service location',
        serviceInStore: 'In-store',
        serviceAtCustomer: 'At customer',
        serviceOnline: 'Online',
        serviceHybrid: 'Hybrid',
        fieldServiceLocation: 'Address / venue',
        serviceLocationPlaceholder: 'e.g. 3F XYZ Building, District X',
        requiresBooking: 'Requires booking',
        requiresBookingHint: 'When on, customers must pick a time slot when ordering via storefront or AI',
        saveEdit: 'Save changes',
        saveCreate: 'Create item',
        createHint: 'Add photos on the detail page after creating',
        editOnWeb: 'Open on desktop to edit advanced settings',
        errors: {
          nameTitle: 'Name required',
          nameBody: 'Product name is required',
          priceTitle: 'Invalid price',
          priceBody: 'Please enter a valid price',
          durationTitle: 'Invalid duration',
          durationBody: 'Please enter a valid number of minutes',
          saveFailedTitle: 'Save failed',
          unknownError: 'Unknown error',
        },
      },
    },
    businessLists: {
      orders: {
        todayTotal: 'Today {amount}',
        filterAll: 'All',
        filterPending: 'Pending',
        filterConfirmed: 'Confirmed',
        filterPaid: 'Paid',
        filterCompleted: 'Completed',
        filterCancelled: 'Cancelled',
        unpaid: 'Unpaid',
        unknownCustomer: 'Unknown customer',
        itemSummaryNone: 'No items',
        itemSummaryMore: '{first} + {total} items · {totalQty} pieces',
        empty: {
          allTitle: 'No orders yet',
          allSub: 'Orders from WhatsApp will appear here',
          pendingTitle: 'No pending orders',
          pendingSub: 'Take a break',
          confirmedTitle: 'No confirmed orders',
          paidTitle: 'No paid orders',
          completedTitle: 'No completed orders',
          cancelledTitle: 'No cancelled orders',
        },
      },
      bookings: {
        allBranches: 'All branches',
        filterToday: 'Today',
        filterUpcoming: 'Upcoming',
        filterPending: 'Pending',
        filterConfirmed: 'Confirmed',
        filterCompleted: 'Completed',
        filterCancelled: 'Cancelled',
        statusPending: 'Pending',
        statusConfirmed: 'Confirmed',
        statusCompleted: 'Completed',
        statusCancelled: 'Cancelled',
        statusNoShow: 'No show',
        fallbackCustomer: 'Unknown customer',
        fallbackService: 'Service',
        cancelReasonPrefix: 'Cancel reason: ',
        dayCountLabel: '{count} bookings',
        btnConfirm: 'Confirm',
        btnComplete: 'Complete',
        btnCancel: 'Cancel',
        btnCheckIn: 'Check in',
        btnStartService: 'Start',
        btnEndService: 'Finish',
        attendanceArrived: 'Arrived',
        attendanceInService: 'In service',
        attendanceLate: 'Late',
        attendanceDone: 'Service done',
        depositDue: 'Deposit due',
        actionFailed: 'That did not work',
        confirmFailed: 'Confirm failed',
        completeFailed: 'Update failed',
        cancelConfirmTitle: 'Cancel booking?',
        cancelConfirmBody: 'Are you sure you want to cancel this booking? The customer will be notified automatically.',
        cancelConfirmAction: 'Cancel booking',
        cancelConfirmKeep: 'Keep it',
        cancelFailed: 'Cancel failed',
        unknownError: 'Unknown error',
        create: 'New booking',
        createFirst: 'Create your first',
        empty: {
          upcomingTitle: 'No upcoming bookings',
          upcomingSub: "Bookings from your storefront, AI, or your own entries will show up here",
          pendingTitle: 'No pending bookings',
          confirmedTitle: 'No confirmed bookings',
          completedTitle: 'No completed bookings',
          cancelledTitle: 'No cancelled bookings',
          noShowTitle: 'No no-shows',
        },
      },
      inquiries: {
        filterActive: 'Active',
        filterOpen: 'New',
        filterInProgress: 'In progress',
        filterResolved: 'Resolved',
        typeInquiry: 'Inquiry',
        typeComplaint: 'Complaint',
        typeFeedback: 'Feedback',
        typeSupport: 'Support',
        priorityHigh: 'High',
        priorityUrgent: 'Urgent',
        resolveConfirmTitle: 'Mark as resolved?',
        resolveConfirmBody: 'Are you sure this inquiry is handled?',
        resolveConfirmAction: 'Resolved',
        resolveConfirmCancel: 'Not yet',
        updateFailed: 'Update failed',
        unknownError: 'Unknown error',
        unknownCustomer: 'Unknown customer',
        resolutionRecord: 'Resolution note',
        viewConversation: 'View chat',
        btnMarkInProgress: 'In progress',
        btnMarkResolved: 'Resolved',
        createBtn: 'New inquiry',
        trackCase: 'Tickets',
        trackDeal: 'Deals',
        filterOverdue: 'Overdue',
        filterMine: 'Mine',
        unassigned: 'Unassigned',
        ownerLabel: 'Owner: {name}',
        assignToMe: 'Assign to me',
        dueIn: 'due in {time}',
        overdueBy: 'overdue {time}',
        stageLead: 'Lead',
        stageProposal: 'Proposal',
        stageNegotiation: 'Negotiation',
        stageWon: 'Won',
        stageLost: 'Lost',
        nextStep: 'Next: {text}',
        noValue: 'No budget set',
        assignFailed: 'Could not assign',
        empty: {
          activeTitle: 'No active inquiries',
          activeSub: 'Customer support tickets and complaints land here; you can add your own too',
          openTitle: 'No new inquiries',
          inProgressTitle: 'Nothing in progress',
          resolvedTitle: 'No resolved records',
          closedTitle: 'No closed records',
        },
      },
      products: {
        availableOfTotal: '{available} / {total} listed',
        searchPlaceholder: 'Search name or category',
        filterAll: 'All',
        filterProduct: 'Products',
        filterService: 'Services',
        tagService: 'Service',
        tagPackage: 'Package · {count} sessions',
        tagInquiryQuote: 'Quote',
        tagMultiOption: 'Variants',
        tagUnlisted: 'Unlisted',
        stateDraft: 'Draft',
        stateArchived: 'Archived',
        channelAiOnly: 'AI only',
        channelStoreOnly: 'Storefront only',
        channelNone: 'No channel',
        filterRestock: 'Needs restock',
        waitingCount: '{count} waiting',
        stockOut: 'Out of stock',
        stockLow: '{count} left',
        stockOk: 'Stock: {count}',
        createBtn: 'New item',
        addFirst: 'Add your first',
        empty: {
          noResultsTitle: 'No results',
          noResultsSub: 'Try a different keyword',
          noProductsTitle: 'No products yet',
          noServicesTitle: 'No services yet',
          productCatalogSub: 'Add products or services to your catalog so customers can see them in chat',
          restockTitle: 'Nothing needs restocking',
          restockSub: 'Every product with a low-stock threshold is above it.',
        },
      },
    },
    customers: {
      title: 'Customers',
      countLabel: '{count}',
      searchPlaceholder: 'Search name, phone, email',
      orderCountOne: '{count} order',
      orderCountOther: '{count} orders',
      emptyNoResults: 'No results',
      emptyNoSearchHint: 'Try a different keyword',
      emptyNoCustomers: 'No customers yet',
      emptyNoCustomersHint: 'Customers appear here after they message you',
      lifecycle: {
        new: 'New',
        active: 'Active',
        atRisk: 'At risk',
        dormant: 'Dormant',
        churned: 'Churned',
      },
    },
    conversations: {
      title: 'Chats',
      pendingCountLabel: '{count} waiting',
      filterInbox: 'Inbox',
      filterPending: 'Pending',
      filterMine: 'Mine',
      filterAll: 'All',
      youPrefix: 'You: ',
      aiPrefix: 'AI: ',
      noMessage: 'No messages',
      unknownCustomer: 'Unknown',
      humanActiveBadge: 'Handled by human',
      draftBadge: 'Draft to approve',
      capturedKnowledgeHeader: 'Knowledge drafts',
      empty: {
        inboxTitle: 'Inbox is clear',
        inboxSub: 'All customer conversations are being handled by AI',
        pendingTitle: 'Nothing waiting for handoff',
        pendingSub: 'Take a break',
        mineTitle: "You're not handling any conversations",
        mineSub: 'Check the Pending tab for customers that need you',
        allTitle: 'No conversations yet',
        allSub: 'Customer messages will appear here',
      },
    },
    login: {
      heroTagline: 'Your WhatsApp AI assistant, built for your business',
      feature1: 'Multilingual AI auto-reply',
      feature2: 'Orders, bookings, and CRM in one place',
      feature3: 'Instant alerts when customers reach out',
      title: 'Sign in',
      subtitle: 'Continue managing your business',
      googleSignIn: 'Sign in with Google',
      orEmail: 'or use email',
      emailPlaceholder: 'your@email.com',
      sendLink: 'Send magic link',
      termsPrefix: 'By signing in you agree to our',
      termsOfService: 'Terms of Service',
      termsAnd: 'and',
      privacyPolicy: 'Privacy Policy',
      checkEmailTitle: 'Check your inbox',
      checkEmailSentTo: "We've sent a sign-in link to {email}",
      checkEmailHint: 'Tap the link and it will open this app automatically.',
      tryDifferentEmail: 'Use a different email',
      loginFailed: 'Sign-in failed',
      googleLaunchFailed: "Couldn't start Google sign-in",
      googleNoAuth: "We didn't receive Google's authorisation",
    },
    updateRequired: {
      title: 'Update required',
      body: 'This version of the app is no longer supported. Update to keep managing your shop. Your data is safe.',
      cta: 'Update now',
    },
    needsOnboarding: {
      title: 'Finish setup on web',
      body: 'Your account is ready, but you need to set up your business on the NovixOne dashboard before you can use the mobile app. It only takes a minute.',
      ctaOpenWeb: 'Open dashboard',
      signOut: 'Sign out',
    },
    loyaltySection: {
      heading: 'Loyalty',
      loadError: "Couldn't load loyalty info",
      notMemberTitle: 'Not a member',
      notMemberHint: 'Auto-enrolled on first earn or redemption.',
      currentPoints: 'Current points',
      lifetimePoints: 'Lifetime points',
      expiringWarning: '{count} points expiring soon',
      expiringEarliest: 'Earliest:',
      unnamedProgram: 'Unnamed programme',
      typeStamp: 'Stamp card',
      stampLabel: 'Stamps',
      currentLabel: 'Current',
      lifetimeLabel: 'Lifetime',
      referralCode: 'Referral code',
      pendingHeading: 'Pending vouchers ({count})',
      pendingPointsAndExpiry: '{points} pts · expires',
      ledgerHeading: 'Recent activity',
      balanceAfter: 'Bal {value}',
      topTierEnteredAt: 'Top tier · since {date}',
      reasonLabels: {
        order_paid: 'Order paid',
        order_completed: 'Order completed',
        appointment_completed: 'Appointment completed',
        signup_bonus: 'Sign-up bonus',
        birthday_bonus: 'Birthday gift',
        referral_earn: 'Referral reward',
        tier_upgrade_bonus: 'Tier-up bonus',
        stamp_earned: 'Stamp earned',
        stamp_complete_bonus: 'Card complete bonus',
        manual_adjustment: 'Manual adjustment',
        bonus: 'Bonus',
        redemption_hold: 'Redemption pending',
        redemption_commit: 'Redemption used',
        redemption_cancel: 'Redemption cancelled',
        redemption: 'Redemption',
        expiry: 'Expired',
      },
      statusLabels: {
        pending: 'Pending',
        approved: 'Approved',
        used: 'Used',
        cancelled: 'Cancelled',
        expired: 'Expired',
      },
    },
    support: {
      analytics: {
        title: 'Trends',
        periodWeek: 'Last 7 days',
        periodMonth: 'Last 30 days',
        empty: 'No data yet',
        labelRevenue: 'Revenue',
        labelOrders: 'Orders',
        labelConversations: 'Chats',
        labelAiContainment: 'AI handled',
        chartDailyRevenue: 'Daily revenue',
        chartDailyConversations: 'Daily chats',
        chartMin: 'Low {value}',
        chartMax: 'High {value}',
        vsPrev: 'vs prev period',
        flat: 'Flat vs prev period',
      },
      knowledgeList: {
        title: 'Knowledge base',
        loadError: "Couldn't load the knowledge base",
        searchPlaceholder: 'Search title, content or keywords',
        training: 'AI is still learning keywords for some entries…',
        emptySearchTitle: 'No matches',
        emptyTitle: 'Knowledge base is empty',
        emptySearchHint: 'Try a different keyword.',
        emptyHint: 'Add FAQs and policies so AI can answer customers automatically.',
        addFirst: 'Add your first entry',
        categoryLabels: {
          faq: 'FAQ',
          policy: 'Policy',
          info: 'Info',
          general: 'General',
        },
        deactivated: 'Deactivated',
        processing: 'Processing',
      },
      knowledgeGaps: {
        title: 'Knowledge gaps',
        hint: 'Questions customers asked that went unanswered, grouped by meaning.',
        emptyTitle: 'No open gaps',
        emptyHint: 'The AI answered everything customers asked — or it has not started yet.',
        loadError: 'Could not load gaps',
        occurrences: (n: number) => (n === 1 ? 'once' : `${n}×`),
        customers: (n: number) => (n === 1 ? '1 customer' : `${n} customers`),
        reopened: 'Answered but still missed',
        reopenedHint: 'The answer exists but the AI still cannot find it — add phrasings to that entry rather than writing a second one.',
        samples: 'What customers said',
        createEntry: 'Write an entry',
        openEntry: 'Open entry',
        dismiss: 'Dismiss',
        filterAll: 'All',
        filterMiss: 'Nothing found',
        filterLowConfidence: 'Low confidence',
        filterEscalation: 'AI gave up',
        filterReopened: 'Answered but still missed',
      },
      knowledgeDrafts: {
        title: 'Awaiting review',
        hint: 'Captured from conversations — nothing goes live until you confirm it.',
        emptyTitle: 'Nothing awaiting review',
        emptyHint: 'New knowledge captured from conversations shows up here.',
        loadError: 'Could not load drafts',
        confidence: (pct: number) => `${pct}% confidence`,
        approve: 'Approve',
        reject: 'Reject',
        approved: 'Approved',
        rejected: 'Rejected',
        actionError: 'Action failed',
      },
      knowledgeEditor: {
        titleNew: 'New knowledge',
        titleEdit: 'Edit knowledge',
        save: 'Save',
        saveError: 'Save failed',
        unknownError: 'Unknown error',
        deleteConfirmTitle: 'Delete knowledge?',
        deleteConfirmBody: "AI won't use this entry to answer customers anymore.",
        deleteConfirmCancel: 'Cancel',
        deleteConfirmAction: 'Delete',
        deleteFailed: 'Delete failed',
        introHeading: 'Teach AI how to answer customers',
        introBody: "Write the title the way customers would ask. The content is how you want AI to reply. Once saved, AI learns to use this for similar questions.",
        titleLabel: 'Title',
        titlePlaceholder: 'e.g. What time do you open?',
        titleHint: "Phrase it the way customers would ask — AI matches similar phrasings automatically.",
        contentLabel: 'Content',
        contentPlaceholder: 'e.g. We\'re open 10am–7pm, closed on Sundays. Public holidays are announced on Instagram.',
        categoryLabel: 'Category',
        categoryLabels: {
          faq: 'FAQ',
          policy: 'Policy',
          info: 'Info',
          general: 'General',
        },
        activeLabel: 'Active',
        activeHint: "When off, AI won't use this entry anymore.",
        trainingNote: 'AI is still learning keywords for this entry…',
        delete: 'Delete knowledge',
      },
      soulEditor: {
        title: 'Tone & guidelines',
        hint: 'These apply to every AI agent. The system basics (language, no fabrication, confirm before ordering) are already enforced.',
        save: 'Save',
        saveError: 'Save failed',
        unknownError: 'Unknown error',
        unsavedTitle: 'Unsaved changes',
        unsavedBody: 'Leaving will discard your unsaved guideline edits.',
        unsavedStay: 'Stay',
        unsavedDiscard: 'Discard',
        toneLabel: 'Tone',
        tones: {
          friendly: 'Friendly',
          professional: 'Professional',
          casual: 'Casual',
          enthusiastic: 'Enthusiastic',
        },
        add: 'Add',
        guidelinesLabel: 'Guidelines',
        guidelinesPlaceholder: 'e.g. Mention seasonal promos proactively',
        guidelinesEmpty: 'None set — system defaults apply automatically',
        guidelinesHint: 'Only rules unique to your business.',
        redLinesLabel: 'Red lines (never do)',
        redLinesPlaceholder: 'e.g. Never quote products from other shops',
        redLinesEmpty: 'No red lines set',
        redLinesHint: 'Things the AI must never do.',
      },
      agentsList: {
        title: 'AI agents',
        introHeading: 'Manage AI agents',
        introBody: 'Each agent has its own persona and skills. Most merchants only need one — tap in to edit the persona and add skills.',
        emptyTitle: 'No agents yet',
        emptyBody: 'Onboarding normally creates one for you. If not, contact support.',
        active: 'Active',
        descriptionMissing: 'No description',
        defaultBadge: 'Default',
        inactive: 'Inactive',
        channelsNone: 'No channel bound (default routing)',
        soulLink: 'Tone & guidelines',
        soulLinkHint: 'Tone, guidelines and red lines — shared by every agent',
      },
      agentsEditor: {
        titleFallback: 'Agent',
        save: 'Save',
        saveError: 'Save failed',
        unknownError: 'Unknown error',
        notFound: 'Agent not found',
        deactivateTitle: 'Deactivate agent?',
        deactivateBody: "When deactivated, this agent won't handle any conversations.",
        deactivateCancel: 'Cancel',
        deactivateAction: 'Deactivate',
        activeLabel: 'Active',
        inactiveLabel: 'Deactivated',
        activeHint: 'This agent is handling customer conversations',
        inactiveHint: "Deactivated agents don't handle any conversations",
        nameLabel: 'Name',
        namePlaceholder: 'e.g. Main assistant',
        descLabel: 'Description',
        descPlaceholder: "Short description of this agent's role",
        personaLabel: 'Persona prompt',
        personaPlaceholder: "Describe in natural language what personality, tone, and behaviours this AI should adopt. These instructions are appended to the system prompt.",
        personaHint: 'Different from AI personality (Soul) — Soul is the shared tone and red lines for the whole shop; this is per-agent specific instructions.',
        skillsLabel: 'Skills',
        skillsAdd: 'Add',
        skillsEmptyTitle: 'No skills yet',
        skillsEmptyBody: 'Skills are scenario-specific instructions — e.g. "Handle complaints", "Booking flow". AI activates them automatically when it detects matching conversations.',
        skillLearningLabel: 'Learning triggers',
        skillDeactivated: 'Deactivated',
        sheetCreateTitle: 'New skill',
        sheetEditTitle: 'Edit skill',
        skillNameLabel: 'Name',
        skillNamePlaceholder: 'e.g. Handle complaints',
        skillInstructionsLabel: 'Instructions',
        skillInstructionsPlaceholder: 'Describe what AI should do in this scenario in natural language. E.g. "When a customer complains, apologise first, then ask for the specifics…"',
        skillInstructionsHint: "Triggers are auto-generated by AI — you don't need to set them manually.",
        skillSaveCreate: 'Create skill',
        skillSaveEdit: 'Save changes',
        skillDelete: 'Delete skill',
        skillDeleteConfirmTitle: 'Delete skill?',
        skillDeleteConfirmBody: 'After deleting "{name}" AI will not use this skill anymore.',
        skillDeleteConfirmCancel: 'Cancel',
        skillDeleteConfirmAction: 'Delete',
        skillDeleteFailed: 'Delete failed',
        defaultBadge: 'Default',
        defaultHint: 'Default agent: answers every conversation without a channel-specific agent; cannot be deactivated.',
        setDefault: 'Set as default',
        cannotDeactivateDefault: 'The default agent cannot be deactivated — set another agent as default first.',
        inactiveCannotBeDefault: 'An inactive agent cannot be the default — activate it first.',
        skillGlobal: 'Global',
        skillTriggerFailed: 'Keyword generation failed',
        skillTriggersLabel: 'Trigger keywords',
        skillTriggersPlaceholder: 'Comma-separated, e.g. refund, complaint',
        skillTriggersHint: 'Leave empty to let the AI generate them. English matches whole words; Chinese matches anywhere.',
        skillActiveLabel: 'Skill enabled',
        skillRegenerate: 'Regenerate keywords',
        skillRegenerateTitle: 'Regenerate keywords?',
        skillRegenerateBody: 'Uses AI credits to regenerate from the instructions. Existing keywords stay live until the new ones land.',
        skillNameTaken: 'A skill with this name already exists — pick another name.',
        skillDeleteGlobalConfirmBody: '"{name}" is a global skill used by every AI agent. This cannot be undone.',
        skillSaveFailed: 'Save failed',
      },
      staffList: {
        title: 'Staff',
        introHeading: 'Manage staff',
        introBody: 'Add your team members and set which services they can provide. They are auto-assigned when bookings come in (from your storefront, AI, or manual entries).',
        emptyTitle: 'No staff yet',
        emptyBody: "Once you add staff, new bookings can be auto-assigned, or customers can pick someone via your storefront or in chat.",
        allServices: 'All services',
        someServices: '{count} services',
        deactivated: 'Deactivated',
      },
      staffEditor: {
        titleNew: 'New staff',
        titleEdit: 'Edit staff',
        save: 'Save',
        saveError: 'Save failed',
        unknownError: 'Unknown error',
        deleteConfirmTitle: 'Delete staff?',
        deleteConfirmBody: 'Delete "{name}" — any unfinished bookings will be reassigned.',
        deleteConfirmCancel: 'Cancel',
        deleteConfirmAction: 'Delete',
        deleteFailed: 'Delete failed',
        nameLabel: 'Name',
        namePlaceholder: 'e.g. Amy',
        phoneLabel: 'Phone',
        phonePlaceholder: 'Personal contact phone',
        emailLabel: 'Email',
        colorLabel: 'Display colour',
        activeLabel: 'Active',
        inactiveLabel: 'Deactivated',
        activeHint: "When off, this staff member won't be assigned to new bookings",
        servicesLabel: 'Available services',
        servicesAll: 'All services',
        servicesSome: 'Specific services',
        pickCategoriesHint: 'Pick categories = automatically include all services in those categories',
        pickServicesHint: 'Or pick individual services',
        noServicesHint: 'No assignable services yet — add items with type Service first under Products',
        delete: 'Delete staff',
      },
      assistant: {
        title: 'AI assistant',
        subtitle: 'Helps you manage products, orders, bookings, and customers',
        voiceLocalePickerTitle: 'Speech recognition language',
        voiceLocaleAuto: 'Auto (device locale)',
        voiceLocaleCancel: 'Cancel',
        voiceRecognitionFailed: 'Speech recognition failed',
        voiceUnsupportedTitle: 'Voice not supported',
        voiceUnsupportedBody: "This device or build doesn't support voice recognition.",
        voiceStartFailed: "Couldn't start voice recording",
        voiceListeningChip: 'Listening… speak now',
        voiceListeningPlaceholder: 'Listening…',
        noPermissionTitle: 'Permission required',
        noCameraPermission: 'Camera permission is required',
        noPhotoPermission: 'Photo library permission is required',
        iCloudTitle: 'Photo still in iCloud',
        iCloudBody: "This photo isn't on your device yet. Open iCloud, wait for it to download, and try again.",
        imageUploadFailed: 'Image upload failed',
        attachSheetTitle: 'Add image',
        attachCamera: 'Camera',
        attachLibrary: 'Photo library',
        attachCancel: 'Cancel',
        thinking: 'AI assistant is thinking…',
        sendFailed: 'Send failed',
        uploadingImage: 'Uploading image…',
        clearTitle: 'Clear conversation?',
        clearBody: "Once cleared, AI won't remember what you discussed before.",
        clearCancel: 'Cancel',
        clearAction: 'Clear',
        clearFailed: 'Clear failed',
        inputPlaceholder: 'Ask AI assistant to do something…',
        imageFallbackPrompt: 'Please take a look at this image',
        emptyTitle: 'AI assistant',
        emptyBody: "Your 24/7 AI teammate\nAsk me anything you want done or learned",
        emptySuggestions: {
          followUp: 'Any customers I should follow up with today?',
          addProduct: 'Take a photo to add a new product (tap 📷 below)',
          tomorrowBookings: 'How many bookings are there tomorrow?',
          lapsedCustomers: "Which customers haven't come back in a month?",
        },
        imageCountBadge: '{count} images',
        localeLabels: {
          zhHK: 'Cantonese (HK)',
          zhTW: 'Traditional Chinese (TW)',
          zhCN: 'Simplified Chinese',
          enUS: 'English (US)',
          enGB: 'English (UK)',
          enAU: 'English (Australia)',
          jaJP: 'Japanese',
          koKR: 'Korean',
          thTH: 'Thai',
          viVN: 'Vietnamese',
        },
      },
    },
    components: {
      phoneInput: {
        placeholder: 'Phone number',
      },
      countryPicker: {
        title: 'Select country / region',
        done: 'Done',
        searchPlaceholder: 'Search country or dial code',
        emptyResults: 'No results',
      },
      topupSheet: {
        openPaymentFailed: "Couldn't open payment",
        unknownError: 'Unknown error',
        title: 'Buy more replies',
        subtitle: 'One-time payment, replies carry across months',
        recommended: 'Recommended',
        replyCountLabel: '{count} AI replies',
        perReply: '{price}/reply',
        footer: "Paid via Stripe — replies are added to your account automatically after payment",
        bundleSmall: 'Small',
        bundleMedium: 'Medium',
        bundleLarge: 'Large',
      },
      customerEditSheet: {
        title: 'Edit customer',
        saveError: 'Save failed',
        unknownError: 'Unknown error',
        fieldName: 'Name',
        fieldNamePlaceholder: 'Customer name',
        fieldPhone: 'Phone',
        fieldEmail: 'Email',
        fieldTags: 'Tags',
        tagInputPlaceholder: 'Type a tag and press enter',
        save: 'Save changes',
      },
      customerPicker: {
        titleDefault: 'Pick a customer',
        addNew: 'New customer',
        searchPlaceholder: 'Search name or phone',
        emptyNoResults: 'No results',
        emptyNoCustomers: 'No customers yet',
        addNewHint: 'Tap "New customer" in the top right to add one',
        newCustomerTitle: 'New customer',
        save: 'Save',
        nameRequiredTitle: 'Name required',
        nameRequiredBody: 'Customer name is required',
        phoneInvalidTitle: 'Invalid phone number',
        phoneInvalidBody: 'Check the country code and local number digit length.',
        createFailedTitle: "Couldn't create",
        nameLabel: 'Name',
        namePlaceholder: 'How you refer to the customer',
        phoneLabel: 'Phone',
        phoneHint: 'With a phone number, future WhatsApp messages from this customer link back automatically',
      },
      notesSection: {
        title: 'Notes',
        composerPlaceholder: 'Write a note about this customer…',
        visibleToAi: 'AI visible',
        hiddenFromAi: 'Hidden from AI',
        addBtn: 'Add',
        loadError: 'Failed to load notes',
        emptyLabel: 'No notes yet',
        deleteConfirmTitle: 'Delete note?',
        deleteConfirmBody: "This can't be undone.",
        deleteConfirmCancel: 'Cancel',
        deleteConfirmAction: 'Delete',
        createFailedTitle: 'Add failed',
        deleteFailedTitle: 'Delete failed',
        unknownError: 'Unknown error',
      },
      memoriesSection: {
        title: 'AI memories · {count}',
        titleNoCount: 'AI memories',
        addBtn: 'Add',
        cancelBtn: 'Cancel',
        keyPlaceholder: 'Key, e.g. payment preference',
        valuePlaceholder: 'Value, e.g. bank transfer',
        saveBtn: 'Save memory',
        loadError: 'Failed to load memories',
        emptyLabel: "No AI memories yet — AI extracts them automatically after conversations",
        deleteConfirmTitle: 'Delete memory?',
        deleteConfirmBody: 'The "{key}" memory will be removed.',
        deleteConfirmCancel: 'Cancel',
        deleteConfirmAction: 'Delete',
        createFailedTitle: 'Add failed',
        deleteFailedTitle: 'Delete failed',
        unknownError: 'Unknown error',
        categoryLabels: {
          preference: 'Preference',
          behavior: 'Behavior',
          personal: 'Personal',
          complaint: 'Complaint',
        },
        sourceManual: 'Manual',
        sourceAi: 'AI extracted',
        confidenceLabel: '{percent}% confidence',
        mentionCount: 'Mentioned {count}×',
      },
    },
    detail: {
      customer: {
        titleFallback: 'Customer',
        loadError: "Couldn't load customer",
        back: 'Back',
        statTotalSpend: 'Total spend',
        statTotalOrders: 'Orders',
        statTotalOrdersSuffix: '',
        statEngagement: 'Engagement score',
        statHealth: 'Health score',
        sectionContact: 'Contact',
        sectionTags: 'Tags',
        sectionActivity: 'Activity',
        sectionTimeline: 'Timeline · {count}',
        contactPhone: 'Phone',
        contactEmail: 'Email',
        notProvided: 'Not provided',
        lastSeen: 'Last seen',
        lastOrder: 'Last order',
        summaryFallback: '(No summary yet)',
        messageCount: '{count} messages',
        orderStatus: {
          pending: 'Pending',
          confirmed: 'Confirmed',
          paid: 'Paid',
          completed: 'Completed',
          cancelled: 'Cancelled',
        },
        conversationStatus: {
          aiActive: 'AI handling',
          pendingHandoff: 'Pending handoff',
          humanActive: 'Human handling',
          resolved: 'Resolved',
        },
        unpaid: 'Unpaid',
        lifecycle: {
          new: 'New',
          active: 'Active',
          atRisk: 'At risk',
          dormant: 'Dormant',
          churned: 'Churned',
        },
        inquiryTypes: {
          inquiry: 'Inquiry',
          complaint: 'Complaint',
          feedback: 'Feedback',
          support: 'Support',
        },
        inquiryStatus: {
          open: 'Open',
          inProgress: 'In progress',
          resolved: 'Resolved',
          closed: 'Closed',
        },
        inquiryPriority: {
          low: 'Low',
          normal: 'Normal',
          high: 'High',
          urgent: 'Urgent',
        },
      },
      conversation: {
        approval: {
          title: 'Needs approval',
          subtitle: 'The AI wants to do this in your system and needs a colleague to approve:',
          approve: 'Approve',
          reject: 'Reject',
          approveConfirmTitle: 'Approve this action?',
          approveConfirmBody: 'It runs in your system right away and the customer is told the result.',
          rejectConfirmTitle: 'Reject this action?',
          rejectConfirmBody: 'Nothing is done; the customer is told a colleague could not approve it.',
          noPermission: "You don't have permission to approve this kind of action",
          expires: 'Valid until {time}',
          done: 'Done ✅',
          failed: 'Failed — handed to a colleague',
          rejected: 'Rejected',
          alreadyDecided: 'Already handled by a colleague',
          expired: 'Expired',
        },
        externalTools: {
          ranInYourSystem: 'Run in your own system',
          args: 'Args',
          result: 'Result',
          noFacts: 'Nothing came back beyond the status.',
          close: 'Close',
          states: {
            read: 'looked up',
            done: 'done',
            awaiting_customer: 'awaiting customer',
            awaiting_approval: 'awaiting approval',
            unavailable: 'unavailable',
            blocked: 'not run',
          },
        },
        customerFallback: 'Customer',
        loadError: "Couldn't load conversation",
        back: 'Back',
        unknownError: 'Unknown error',
        takeoverClaimedTitle: 'Already taken',
        takeoverClaimedBody: "Another teammate has already taken over this conversation.",
        takeoverClaimedOk: 'Got it',
        takeoverFailed: 'Takeover failed',
        returnToAiFailed: 'Action failed',
        sendFailed: 'Send failed',
        draftFailed: "AI couldn't draft",
        draftBtnIdle: 'AI draft reply',
        pendingDraftTitle: 'AI draft · waiting for your approval',
        pendingDraftSend: 'Send',
        pendingDraftDiscard: 'Discard',
        pendingDraftHint: 'Edit it here if needed. Sending does not take the conversation over — the AI keeps drafting.',
        draftBtnLoading: 'AI is thinking…',
        inputPlaceholder: 'Type a reply...',
        returnToAi: 'Hand back to AI',
        takeoverBtn: 'Take over',
        claimBtn: 'Take over',
        conversationResolved: 'Conversation resolved',
        emptyMessages: 'No messages',
        episodeStarted: 'New conversation',
        episodePrevious: 'Previous: {reason} · {handled}',
        episodeCloseReasons: { settled: 'done (auto)', staff: 'closed by staff', ai: 'closed by AI', api: 'closed by system', idle: 'closed when idle', merged: 'earlier chat' },
        episodeHandledBy: { ai_only: 'AI only', handed_off: 'handed to staff', human: 'staff' },
        aiAssistantLabel: 'AI assistant',
        fromWhatsAppApp: 'From the WhatsApp Business App',
        importedFromWhatsAppApp: 'Imported from the WhatsApp Business App',
        rateUp: 'Good reply',
        rateDown: 'Something was wrong',
        rateWithdraw: 'Remove rating',
        rateReasonTitle: 'What went wrong?',
        rateReasons: {
          wrong_info: 'Wrong information',
          wrong_tone: 'Wrong tone',
          should_escalate: 'Should have handed off',
          hallucination: 'Made something up',
          other: 'Other',
        },
        rateFailed: 'Could not save rating',
        mediaAudio: 'Voice message',
        mediaDocument: 'Document attachment',
        mediaTapOpen: 'Tap to open',
        insightsTitle: 'AI insights',
        insightsUrgentBadge: 'Urgent follow-up needed',
        insightsCountBadge: '{count} follow-ups',
        insightsUnhappyBadge: 'Customer unhappy',
        objectionsLabel: 'Customer concerns',
        actionsSuggestionLabel: 'Suggested follow-ups',
        actionComplete: 'Mark done',
        actionDismiss: 'Dismiss',
        purchaseIntent: '{percent}% purchase intent',
        urgencyHigh: 'High urgency',
        urgencyMedium: 'Medium urgency',
        sentiment: {
          veryNegative: 'Very negative',
          negative: 'Negative',
          neutral: 'Neutral',
          positive: 'Positive',
          veryPositive: 'Very positive',
        },
        priority: {
          low: 'Low',
          medium: 'Med',
          high: 'High',
          urgent: 'Urgent',
        },
        statusPill: {
          pendingHandoff: 'Waiting',
          humanActive: 'Human',
          aiActive: 'AI',
          resolved: 'Resolved',
        },
        statusLabel: {
          aiActive: 'AI handling',
          pendingHandoff: 'Waiting for handoff',
          humanActive: 'Human handling',
          resolved: 'Resolved',
        },
        attachImage: 'Attach',
        imageReady: 'Image ready',
        imagePermDeniedTitle: 'Photo access needed',
        imagePermDeniedBody: 'Allow NovixOne to access photos in Settings',
        imageTooLargeTitle: 'Image too large',
        imageTooLargeBody: 'Please pick an image under 8 MB',
        resolveBtn: 'Resolve',
        resolveConfirmTitle: 'Resolve conversation?',
        resolveConfirmBody: 'It will move to the resolved list. You can reopen anytime.',
        resolveConfirmOk: 'Resolve',
        resolveFailed: "Couldn't resolve",
        cancel: 'Cancel',
        moreActions: 'More actions',
        actionTransfer: '🔁 Transfer to teammate',
        actionSnooze: '⏰ Snooze',
        actionUnsnooze: '🔔 Unsnooze',
        snoozeTitle: 'Snooze this conversation',
        snoozeBody: 'Snoozed conversations leave the default inbox',
        snooze1h: '1 hour',
        snooze4h: '4 hours',
        snoozeTomorrow: 'Tomorrow 9 AM',
        snoozeFailed: "Couldn't snooze",
        transferComingSoonTitle: 'Teammate picker coming soon',
        transferComingSoonBody: 'Please use the desktop dashboard to transfer for now',
      },
      product: {
        titleFallback: 'Item',
        loadError: "Couldn't load product",
        back: 'Back',
        unknownError: 'Unknown error',
        deleteConfirmTitle: 'Unlist this item?',
        deleteConfirmBody: "Customers won't see it anymore. You can re-list it anytime from the edit form.",
        deleteConfirmCancel: 'Cancel',
        deleteConfirmAction: 'Unlist',
        deleteFailedTitle: 'Failed',
        noPermissionTitle: 'Permission required',
        noCameraPermission: 'Camera permission is required',
        noPhotoPermission: 'Photo library permission is required',
        iCloudTitle: 'Photo still in iCloud',
        iCloudBody: "This photo isn't on your device yet. Open iCloud, wait for it to download, and try again.",
        uploadFailedTitle: 'Upload failed',
        imageActionSheet: {
          camera: 'Camera',
          library: 'Photo library',
          cancel: 'Cancel',
          addImageTitle: 'Add photo',
        },
        deleteImageTitle: 'Delete this photo?',
        deleteImageBody: "This can't be undone.",
        genericFailed: 'Failed',
        uploading: 'Uploading…',
        addFirstImage: 'Add your first photo',
        tagService: 'Service',
        tagPackage: 'Package · {count} sessions',
        tagInquiryQuote: 'Quote',
        tagMultiOption: 'Variants',
        tagUnlisted: 'Unlisted',
        stateDraft: 'Draft',
        stateArchived: 'Archived',
        channelAiOnly: 'AI only',
        channelStoreOnly: 'Storefront only',
        channelNone: 'No channel',
        perfTitle: 'Last 30 days',
        perfUnits: 'Sold',
        perfRevenue: 'Revenue',
        perfWaiting: 'Waiting',
        perfNoSales: 'Nothing sold in the last 30 days.',
        perfLastSold: 'Last sold {date}',
        aiPreviewTitle: 'What the AI sees',
        aiPreviewShow: 'Show what the AI receives',
        aiPreviewHide: 'Hide',
        aiPreviewUnreachable: 'The AI cannot reach this product — that is state or channel, not wording.',
        aiPreviewReadiness: '{ready}/{of} ready',
        openOnDesktop: 'Continue on a computer',
        openOnDesktopHint: 'Full editing (specs, modifiers, scheduled prices) lives on the web',
        durationMinutes: '{minutes} min',
        sectionDescription: 'Description',
        sectionVariants: 'Variants',
        variantsEmpty: 'No variants yet — e.g. different sizes, colours, durations',
        addVariant: 'Add variant',
        stockOut: 'Out of stock',
        stockLow: '{count} left',
        stockOk: 'Stock: {count}',
        stockRemaining: '{count} left',
        stockTotal: 'Stock: {count}',
        variantAvailable: 'Listed',
        variantUnavailable: 'Unlisted',
        variantSheet: {
          createTitle: 'New variant',
          editTitle: 'Edit variant',
          nameLabel: 'Name',
          namePlaceholder: 'e.g. Large / 60 min / Red',
          priceLabel: 'Price (leave blank = same as parent)',
          pricePlaceholder: '0',
          stockLabel: 'Stock',
          stockPlaceholder: "Blank = don't track stock",
          available: 'Listed',
          unavailable: 'Unlisted',
          saveCreate: 'Create variant',
          saveEdit: 'Save changes',
          delete: 'Delete variant',
          deleteConfirmTitle: 'Delete variant?',
          deleteConfirmBody: "Deleting \"{name}\" can't be undone.",
          deleteConfirmCancel: 'Cancel',
          deleteConfirmAction: 'Delete',
          deleteFailed: 'Delete failed',
          createFailed: 'Create failed',
          saveFailed: 'Save failed',
          unknownError: 'Unknown error',
        },
      },
      order: {
        titleFallback: 'Order',
        loadError: "Couldn't load order",
        back: 'Back',
        unknownError: 'Unknown error',
        unknownCustomer: 'Unknown customer',
        confirmFailed: 'Confirm failed',
        updateFailed: 'Update failed',
        cancelFailed: 'Cancel failed',
        markPaidTitle: 'Mark as paid?',
        markPaidBody: "Once marked as paid, the customer will get a confirmation notification.",
        markPaidCancel: 'Cancel',
        markPaidConfirm: 'Paid',
        cancelOrderTitle: 'Cancel order?',
        cancelOrderBody: "This can't be undone and the customer will be notified automatically.",
        cancelOrderCancel: 'Keep it',
        cancelOrderAction: 'Cancel order',
        actionConfirm: 'Confirm order',
        actionMarkPaid: 'Mark paid',
        actionShip: 'Mark shipped',
        actionComplete: 'Complete',
        actionCancel: 'Cancel order',
        sectionItems: 'Items',
        itemCount: '{count}',
        total: 'Total',
        collectedLabel: 'Collected',
        outstandingLabel: 'Outstanding',
        overpaidLabel: 'Overpaid (refund owed)',
        revisionLabel: 'Amended {count}×',
        sectionOther: 'Other info',
        paymentMethodLabel: 'Payment method',
        notesLabel: 'Notes',
        status: {
          pending: 'Pending',
          confirmed: 'Confirmed',
          paid: 'Paid',
          completed: 'Completed',
          cancelled: 'Cancelled',
        },
        unpaid: 'Unpaid',
      },
    },
    home: {
      welcome: {
        greetingNight: 'Late night',
        greetingMorning: 'Good morning',
        greetingAfternoon: 'Good afternoon',
        greetingEvening: 'Good evening',
        fallbackName: 'there',
        tagline: 'Working with NovixOne today',
      },
      aiActions: {
        sectionLabel: 'AI suggestions',
        itemCountLabel: '{count}',
        empty: 'No follow-ups to suggest yet — AI will surface the customers most worth your time after each conversation',
        types: {
          followUp: 'Follow up',
          winBack: 'Win back',
          crossSell: 'Cross-sell',
          resolveIssue: 'Resolve issue',
          nurture: 'Nurture',
          thankVip: 'Thank VIP',
          checkSatisfaction: 'Check in',
        },
        priority: {
          low: 'Low',
          medium: 'Med',
          high: 'High',
          urgent: 'Urgent',
        },
      },
      attention: {
        atRisk: 'At risk',
        highIntent: 'High intent',
        openInquiries: 'Open inquiries',
      },
      bookings: {
        sectionLabel: "Today's bookings",
        viewAll: 'View all',
        statusPending: 'Pending',
        statusConfirmed: 'Confirmed',
        fallbackCustomer: 'Unknown customer',
        fallbackService: 'Service',
      },
      stats: {
        sectionLabel: "Today's snapshot",
        revenue: 'Revenue today',
        orders: 'Orders today',
        conversations: 'Chats today',
        quotaUsed: 'Usage this month',
        trendsTitle: 'Trends',
        trendsSub: 'Revenue, chats, and orders over the last 7 / 30 days',
        couponCheckTitle: 'Check a coupon',
        couponCheckSub: 'Verify a code a customer shows at the counter',
      },
      activeConvs: {
        countLabel: '{count} active conversations',
        viewAll: 'View all chats →',
      },
      followUps: {
        sectionLabel: 'Follow-ups today',
        onRecord: 'Follow-ups',
        noneOnRecord: 'Nothing outstanding on this order.',
        viewAll: 'View all',
        mine: 'Mine',
        overdue: '{count} overdue',
        empty: 'Nothing due today',
        noDate: 'No date',
        done: 'Done',
        markDone: 'Mark done',
        laterAction: 'Follow up later',
        created: 'Follow-up set',
        outcomePrompt: 'What happened?',
        outcomes: {
          done: 'Done',
          no_answer: 'No answer',
          rescheduled: 'Rescheduled',
          not_needed: 'Not needed',
        },
        quick: {
          hour: 'In 1 hour',
          tonight: 'Tonight',
          tomorrow: 'Tomorrow',
          nextWeek: 'Next Monday',
        },
      },
      pending: {
        sectionLabel: 'Waiting for handoff',
        viewAll: 'View all',
        noMessage: 'No messages',
        unknownCustomer: 'Unknown',
      },
      quota: {
        sectionLabel: 'AI reply usage',
        thisMonth: 'This month',
        topupRemaining: 'Plus {count} top-up replies available',
        topup: 'Buy more replies',
        topupWarn: 'Running low — buy more replies',
      },
      fab: {
        askAssistant: 'Ask AI',
      },
      channelDown: {
        title: 'WhatsApp {number} is disconnected',
        body: 'The AI is not answering customers on this number. Reconnect it on the web: Settings › Channels.',
      },
      aiPause: {
        title: 'AI is paused',
        hint: 'New messages will wait for you to handle',
        hintWithDuration: 'Paused for {duration} · new messages will wait for you',
        lessThanMinute: 'less than a minute',
        resume: 'Resume AI',
      },
    },
    bookingAdvanced: {
      cancel: 'Cancel',
      save: 'Save',
      keep: 'Keep',
      unknownError: 'Something went wrong. Please try again.',
      failedTitle: 'Could not do that',
      reasonLabel: 'Reason (required)',
      reasonPlaceholder: 'e.g. goodwill session, booking mistake',
      waitlist: {
        title: 'Waitlist',
        openButton: 'Waitlist · {count}',
        filterOpen: 'Waiting',
        filterActive: 'Not told yet',
        filterNotified: 'Notified',
        filterConverted: 'Booked',
        filterExpired: 'Expired',
        filterCancelled: 'Closed',
        filterAll: 'All',
        allServices: 'All services',
        empty: 'Nobody is waiting',
        emptyHint: 'Add a customer when no slot suits them — you can tell them as soon as one opens.',
        dateWindow: '{from} – {to}',
        anyTime: 'Any time',
        timeWindow: 'Start {from}–{to}',
        timeFromOnly: 'Start from {from}',
        timeToOnly: 'Start by {to}',
        everyDay: 'Any day',
        notifiedTimes: 'Notified {count}×',
        anyStaff: 'Any staff',
        status: {
          active: 'Waiting',
          notified: 'Notified',
          converted: 'Booked',
          expired: 'Expired',
          cancelled: 'Closed',
        },
        add: 'Add',
        addTitle: 'Add to waitlist',
        customer: 'Customer',
        customerPlaceholder: 'Pick a customer',
        service: 'Service',
        servicePlaceholder: 'Pick a service',
        staff: 'Staff',
        branch: 'Branch',
        dateFrom: 'From',
        dateTo: 'Until',
        earliestStart: 'Earliest start',
        latestStart: 'Latest start',
        weekdays: 'Days that suit',
        weekdayShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        note: 'Note',
        notePlaceholder: 'e.g. prefers a window seat',
        submit: 'Add to waitlist',
        added: 'Added to the waitlist',
        alreadyOn: 'This customer is already on this waitlist',
        prefsUpdated: 'Updated this customer’s waitlist preferences',
        closeEntry: 'Close',
        closeConfirmTitle: 'Close this entry?',
        closeConfirmBody: 'The customer stays in the history but is no longer matched to freed slots.',
        remove: 'Remove',
        removeConfirmTitle: 'Remove this entry?',
        removeConfirmBody: 'The entry is deleted for good.',
        notify: 'Notify',
        notifyTitle: 'Tell {name} about a slot',
        notifyHint: 'Nothing is reserved — the customer books it themselves. The slot is checked before anything is sent.',
        slotDate: 'Date',
        slotTime: 'Start time',
        notifySend: 'Send',
        notified: 'Message sent',
        matchesTitle: '{count} waiting for this slot',
        errors: {
          SLOT_TAKEN: 'This slot is no longer free',
          SLOT_PAST: 'This slot has already started',
          OPTED_OUT: 'This customer does not accept marketing messages',
          ALREADY_NOTIFIED: 'This customer was already told about this slot',
          ENTRY_CLOSED: 'This waitlist entry has ended',
          NOT_A_MATCH: 'This slot is for another service or branch',
          NOT_SENT: 'Could not send (no usable channel, or the template is not approved)',
          SEND_FAILED: 'Sending failed — try again',
          SLOT_NOT_FOUND: 'Unknown service, branch or staff for this slot',
          APPOINTMENT_NOT_FOUND: 'That booking no longer exists',
          INVALID_RANGE: 'The date range is not valid',
          INVALID_TIME: 'The time is not valid',
          INVALID_WEEKDAYS: 'Pick at least one day',
          INVALID_DATE: 'The dates are not valid',
          NOT_A_SERVICE: 'Only a bookable service can have a waitlist',
          LOCATION_NOT_FOUND: 'Unknown branch',
          LOCATION_REQUIRED: 'Pick a branch',
          MODULE_DISABLED: 'Advanced booking is switched off',
          NOT_FOUND: 'This waitlist entry no longer exists',
          FORBIDDEN: 'You do not have permission to do this',
        },
      },
      packages: {
        sectionTitle: 'Packages ({count})',
        left: '{left} left',
        usage: '{used} used · {reserved} booked · {total} total',
        expires: 'Valid until {date}',
        noExpiry: 'No expiry',
        target: 'For {service}',
        status: {
          active: 'Active',
          completed: 'Used up',
          cancelled: 'Cancelled',
          expired: 'Expired',
        },
        cancelReason: {
          staff: 'Cancelled by staff',
          order_refunded: 'Order refunded',
          order_cancelled: 'Order cancelled',
          payment_voided: 'Payment voided',
        },
        screenTitle: 'Package',
        bookingsTitle: 'Bookings with this package',
        noBookings: 'No booking has used this package yet',
        manage: 'Adjust',
        sessions: 'Sessions',
        sessionsHelp: '+ gives sessions back to the customer, − deducts them',
        newExpiry: 'New expiry: {date}',
        extendBy: '+{days} days',
        keepExpiry: 'Keep',
        apply: 'Save change',
        cancelPackage: 'Cancel package',
        cancelConfirmTitle: 'Cancel this package?',
        cancelConfirmBody: 'No new booking can use it. {reserved} upcoming booking(s) keep their session.',
        reactivate: 'Reactivate',
        reviveConfirmTitle: 'Reactivate a refunded package?',
        reviveConfirmBody: 'This package was cancelled because its order was refunded, cancelled or its payment voided. Reactivating gives those sessions away again.',
        reviveConfirm: 'Reactivate anyway',
        reasonRequired: 'Enter a reason first',
        saved: 'Package updated',
        errors: {
          OVER_TOTAL: 'Not enough sessions left — upcoming bookings already hold some of them',
          NEGATIVE: 'Sessions used cannot go below 0',
          BELOW_SPENT: 'Completed visits spent these sessions — undo those visits instead',
          EXPIRY_BEFORE_BOOKING: 'An upcoming booking uses this package after that date',
          EXPIRY_PASSED: 'Its validity has passed — extend it to reactivate',
          REVIVE_NEEDS_CONFIRM: 'Its order was refunded or cancelled — confirm to reactivate',
          NOT_FOUND: 'This package no longer exists',
          REASON_REQUIRED: 'Enter a reason first',
          NOTHING_TO_CHANGE: 'Nothing to change',
          BAD_EXPIRY: 'The new expiry date is not valid',
          FORBIDDEN: 'Only people who can refund orders can adjust packages',
        },
      },
      booking: {
        title: 'Booking',
        loadError: 'Could not load this booking',
        back: 'Back',
        customer: 'Customer',
        service: 'Service',
        when: 'When',
        staff: 'Staff',
        branch: 'Branch',
        amount: 'Amount',
        packagePaid: 'Package · {n}/{m}',
        packagePaidShort: 'Package',
        deposit: 'Deposit',
        depositDue: 'Deposit due',
        depositPaid: 'Deposit paid',
        order: 'Order',
        seriesLabel: 'Recurring · session {i} of {t} · {frequency}',
        seriesFrequency: {
          weekly: 'Weekly',
          biweekly: 'Every 2 weeks',
          triweekly: 'Every 3 weeks',
          monthly: 'Monthly',
        },
        seriesStatus: {
          active: 'In progress',
          completed: 'Completed',
          cancelled: 'Cancelled',
        },
        seriesSessions: 'All sessions',
        seriesThis: 'This booking',
        cancelRemaining: 'Cancel remaining sessions',
        cancelRemainingConfirm: 'Cancel the remaining {count} session(s)? Past and completed sessions are kept; their orders are cancelled too.',
        cancelRemainingDone: '{count} session(s) cancelled',
        ordersHeld: 'These orders hold a payment — refund them from the order: {orders}',
        openWaitlist: 'Waitlist',
        errors: {
          SERIES_ENDED: 'This recurring booking has already ended',
          NOTHING_TO_CANCEL: 'No upcoming session is still booked',
          FORBIDDEN: 'You do not have permission to do this',
          NOT_FOUND: 'This booking no longer exists',
        },
      },
    },
  },

  loyalty: {
    title: 'Loyalty',
    subtitle: 'Reward your customers and grow lifetime value',

    filters: {
      member: 'Members',
      topTier: 'VIP & above',
      highValueNonMember: 'High-spend non-members',
    },
    sort: {
      lifetimePoints: 'Lifetime points',
    },

    tier: {
      label: 'Tier',
      notMember: 'Not a member',
      nextTier: 'Next tier: {name}',
      pointsToNext: '{count} points to {name}',
      benefits: 'Benefits',
      rankFallback: 'Tier {rank}',
    },

    points: {
      balance: 'Balance',
      lifetime: 'Lifetime points',
      earned: '+{count} pts',
      redeemed: '−{count} pts',
      expiringSoon: '{count} pts expiring on {date}',
      label: 'Points',
      labelShort: 'pts',
    },

    member: {
      enrolledSince: 'Member since {date}',
      pending: 'Pending',
      paused: 'Paused',
      expired: 'Expired',
      banned: 'Banned',
    },

    program: {
      title: 'Program',
      create: 'Create program',
      edit: 'Edit program',
      active: 'Active',
      inactive: 'Inactive',
      autoEnroll: {
        label: 'Auto-enrol customers',
        description: 'Customers automatically join the program on their first qualifying transaction.',
      },
    },

    backfill: {
      stepTitle: 'Past customer history',
      stepSubtitle: 'How should existing customer transactions be handled?',

      optionForward: 'Start fresh from today',
      optionForwardDescription: 'Only new transactions will earn points',
      option3Months: 'Past 3 months',
      option6Months: 'Past 6 months',
      option12Months: 'Past 12 months',
      optionAll: 'All historical transactions',
      recommended: 'Recommended',

      previewLoading: 'Calculating…',
      previewLabel: 'We’ll process approximately:',
      previewOrders: '{count} orders',
      previewAppointments: '{count} appointments',
      previewMembers: '{count} customers will be enrolled',
      previewPoints: '{count} points awarded',
      previewTierUpgrades: '{count} customers will reach a tier',
      previewEmpty: 'No qualifying transactions in this window',

      confirmTitle: 'Confirm backfill',
      confirmDescription: 'Once started this cannot be undone. Customers will see their new member status the next time you reach them.',
      confirmCta: 'Run backfill',

      running: 'Running backfill…',
      completedTitle: 'Backfill completed',
      completedDescription: '{orders} orders and {appointments} appointments processed. {members} customers enrolled. {points} points awarded.',

      disclosure: 'Past transactions will be converted to points using your current earn rate. Customers will see their member status the next time you contact them.',
    },

    empty: {
      members: {
        title: 'No members yet',
        description: 'Members will appear here once your customers start earning points.',
      },
      rewards: {
        title: 'No rewards configured',
        description: 'Add rewards customers can redeem with their points.',
        cta: 'Add reward',
      },
      history: {
        title: 'No activity yet',
        description: 'Points earned, redeemed and expired will appear here.',
      },
    },

    errors: {
      insufficientPoints: 'Not enough points',
      notEnrolled: 'Not a member of this program',
      programInactive: 'Program is currently inactive',
      backfillFailed: 'Backfill could not be completed. Please try again.',
    },

    reasons: {
      order_paid: 'Order paid',
      order_completed: 'Order completed',
      appointment_completed: 'Appointment completed',
      signup_bonus: 'Welcome bonus',
      birthday_bonus: 'Birthday bonus',
      review_bonus: 'Review bonus',
      referral_earn: 'Referral reward',
      tier_upgrade_bonus: 'Tier upgrade bonus',
      stamp_earned: 'Stamp earned',
      stamp_complete_bonus: 'Stamp card completed',
      manual_adjustment: 'Manual adjustment',
      bonus: 'Bonus',
      redemption_hold: 'Reward held',
      redemption_commit: 'Reward redeemed',
      redemption_cancel: 'Reward cancelled',
      expiry: 'Points expired',
      backfill_order: 'Past order',
      backfill_appointment: 'Past appointment',
    },
  },

  memberPortal: {
    signIn: {
      title: 'Sign in to your account',
      subtitle: 'Enter the email or phone number you use with this merchant. We’ll send you a one-time code.',
      contactLabel: 'Email or phone number',
      contactPlaceholder: 'you@example.com or +852 5555 1234',
      sendCode: 'Send code',
      sending: 'Sending…',
      contactInvalid: 'Enter a valid email or phone number.',
      rateLimited: 'Too many attempts. Try again in an hour.',
      genericError: 'Something went wrong. Please try again.',
      privacyNote: 'Only the contact you’ve already shared with this merchant can sign in.',
      waVerify: {
        primaryCta: 'Continue with WhatsApp',
        emailFallback: 'Use email instead',
        phoneLabel: 'Mobile number',
        phonePlaceholder: '+852 5555 1234',
        phoneInvalid: 'Enter a valid mobile number.',
        initSubmit: 'Continue',
        initSubmitting: 'Preparing…',
        verifyTitle: 'Verify via WhatsApp',
        verifySubtitle: 'Tap below to send a one-tap verification message. We’ll sign you in as soon as it arrives.',
        tapToSend: 'Tap to send via WhatsApp',
        qrLabel: 'Or scan this QR with your phone',
        expiresIn: 'Expires in {seconds}s',
        expired: 'Verification expired. Please start again.',
        mismatch: 'The phone you entered didn’t match the WhatsApp account that sent the message. Please retry.',
        changePhone: 'Use a different number',
        waiting: 'Waiting for your message…',
        verified: 'Verified — signing you in.',
      },
    },
    verify: {
      title: 'Enter your code',
      subtitle: 'We sent a 6-digit code to {contact}. It expires in 5 minutes.',
      codeLabel: 'Verification code',
      codePlaceholder: '123456',
      submit: 'Verify',
      submitting: 'Verifying…',
      resend: 'Resend code',
      resendIn: 'Resend available in {seconds}s',
      invalidCode: 'That code didn’t match. Try again.',
      tooManyAttempts: 'Too many attempts. Request a new code.',
      edit: 'Use a different contact',
    },
    dashboard: {
      welcomeBack: 'Welcome back, {name}',
      welcomeBackNoName: 'Welcome back',
      balanceLabel: 'Available points',
      lifetimeLabel: 'Lifetime points',
      progressToNext: '{count} pts to {name}',
      atTopTier: 'You’ve reached the top tier — thanks for being a regular.',
      yourRewardsHeader: 'Rewards within reach',
      viewAllRewards: 'See all rewards',
      recentActivityHeader: 'Recent activity',
      viewAllActivity: 'See all activity',
      noRecentActivity: 'No activity yet — your earns and redemptions will appear here.',
    },
    rewards: {
      title: 'Rewards',
      emptyTitle: 'No rewards available right now',
      emptyDescription: 'Check back later — the merchant will add rewards as the programme grows.',
      pointsCost: '{points} pts',
      redeem: 'Redeem',
      redeeming: 'Redeeming…',
      insufficient: 'Not enough points',
      confirmTitle: 'Confirm redemption',
      confirmDescription: 'Redeem {name} for {points} points? You’ll get a voucher code to show at the counter.',
      confirmCta: 'Confirm',
      successTitle: 'Voucher ready',
      successDescription: 'Show this code at the counter to redeem.',
      voucherCodeLabel: 'Voucher code',
      voucherExpiresLabel: 'Expires {date}',
      backToRewards: 'Back to rewards',
    },
    history: {
      title: 'Activity',
      filterAll: 'All',
      filterEarn: 'Earned',
      filterRedeem: 'Redeemed',
      filterExpiry: 'Expired',
      emptyTitle: 'No activity yet',
      emptyDescription: 'Your earns, redemptions and expiries will appear here.',
      loadMore: 'Load more',
    },
    settings: {
      title: 'Settings',
      languageSectionTitle: 'Language',
      languageSectionHint: 'Affects only how this portal is displayed to you on this device.',
      dataSectionTitle: 'Your data',
      exportData: 'Download my loyalty data',
      exportFormatJson: 'JSON',
      exportFormatCsv: 'CSV',
      exportHint: 'A copy of every point you’ve earned, redeemed or had expire — useful for your records.',
      accountSectionTitle: 'Account',
      signOut: 'Sign out',
      signingOut: 'Signing out…',
    },
    nav: {
      dashboard: 'Home',
      bookings: 'Bookings',
      orders: 'Orders',
      offers: 'Offers',
      rewards: 'Rewards',
      history: 'Activity',
      settings: 'Settings',
    },
    offers: {
      title: 'My offers',
      walletHeader: 'Your offers',
      publicHeader: 'Open offers',
      historyHeader: 'Used',
      emptyTitle: 'No offers yet',
      emptyDescription: 'Offers you earn or claim will show up here.',
      claimTitle: 'Have a code?',
      claimPlaceholder: 'Enter code',
      claimSubmit: 'Claim',
      claimSubmitting: 'Claiming…',
      claimSuccess: 'Added to your wallet',
      expiresLabel: 'Expires {date}',
      noExpiry: 'No expiry',
      minSpendLabel: 'Min spend {amount}',
      autoApplyTag: 'Auto-applies',
      copyCode: 'Copy code',
      codeCopied: 'Copied!',
      codeCopiedAnnouncement: 'Coupon code {code} copied to clipboard',
      useNow: 'Use now',
      useNowToastTitle: 'Coupon applied',
      useNowToastDescription: 'Added to your cart — saving will show at checkout.',
      identityHint: 'Signed in as {contact}',
      offerStatusAvailable: 'Available',
      offerStatusReserved: 'In use',
      offerStatusUsed: 'Used',
      offerStatusExpired: 'Expired',
      offerStatusRevoked: 'Withdrawn',
      discountPercent: '{value}% off',
      discountPercentCap: '{value}% off (max {cap})',
      discountFixed: '{value} off',
      discountFreeShipping: 'Free shipping',
      historySavedLabel: 'Saved {amount}',
      discountBogoFree: 'Buy {n} get 1 free',
      discountBogoPct: 'Buy {n}, cheapest {pct}% off',
      loyaltyHeader: 'Points rewards',
      loyaltyHint: 'Vouchers redeemed with your points — usable at checkout.',
      loyaltyClaimedTag: 'On an order',
      loyaltyCounterTag: 'Redeem in store',
    },
    bookings: {
      title: 'My bookings',
      upcomingHeader: 'Upcoming',
      pastHeader: 'Past',
      emptyTitle: 'No bookings yet',
      emptyDescription: 'Your upcoming and past bookings will show up here.',
      browseServices: 'Browse services',
      statusPending: 'Awaiting confirmation',
      statusConfirmed: 'Confirmed',
      statusCompleted: 'Completed',
      statusCancelled: 'Cancelled',
      statusNoShow: 'No-show',
      detail: {
        title: 'Booking detail',
        referenceLabel: 'Reference',
        backToList: 'Back to bookings',
        notesLabel: 'Notes',
        depositLabel: 'Deposit',
        manageHeader: 'Manage',
        cancelButton: 'Cancel booking',
        cancelling: 'Cancelling…',
        cancelConfirmTitle: 'Cancel this booking?',
        cancelConfirmBody: 'You\'re cancelling {reference}. This cannot be undone.',
        cancelConfirmCta: 'Yes, cancel',
        cancelKeep: 'Keep booking',
        cancelReasonLabel: 'Reason (optional)',
        cancelReasonPlaceholder: 'Anything you\'d like to share',
        cancelSuccess: 'Booking cancelled.',
        cancelError: 'Couldn\'t cancel — please try again.',
        outsideWindowError: 'You\'re past the cancellation window. Please contact the merchant.',
        contactShopToCancel: 'This booking can\'t be cancelled online. Please contact the shop to cancel it.',
        cancelWindowNote: 'Cancel up to {hours}h before the booking for a full refund.',
        cancelDisabledNote: 'This merchant doesn\'t allow online cancellation. Contact them directly.',
        withdrawRequestNote: 'The shop hasn\'t confirmed this booking yet — you can withdraw the request at any time.',
        cancelSuccessRefundLine: 'Per the cancellation policy, you\'ll receive a {pct}% refund. The merchant will process it in a few business days.',
        cancelSuccessNoRefund: 'Per the cancellation policy, this cancellation is non-refundable.',
        cancelSuccessNoPolicy: 'Cancellation recorded. Please contact the merchant to arrange any refund.',
        cancelSuccessClose: 'Done',
        verifyToManageNote: 'Verify your phone via WhatsApp to manage this booking.',
        verifyToManageCta: 'Verify now',
        contactMerchantHeader: 'Contact the merchant',
        contactWhatsApp: 'Open WhatsApp',
      },
    },
    orders: {
      title: 'My orders',
      activeHeader: 'In progress',
      pastHeader: 'Past',
      emptyTitle: 'No orders yet',
      emptyDescription: 'Orders you place with this merchant will appear here.',
      browseProducts: 'Browse products',
      itemsSummary: '{count} item(s)',
      statusPending: 'Awaiting payment',
      statusConfirmed: 'Confirmed',
      statusPaid: 'Paid',
      statusShipped: 'Shipped',
      statusCompleted: 'Completed',
      statusCancelled: 'Cancelled',
      statusExpired: 'Expired',
      statusRefunded: 'Refunded',
    },
    poweredBy: 'Powered by NovixOne',

    receiptCta: {
      title: 'Track your rewards',
      description: 'See your points, browse rewards and redeem vouchers any time.',
      button: 'Open my rewards',
    },

    voucherCommit: {
      title: 'Apply voucher',
      codeLabel: 'Voucher code',
      codePlaceholder: 'ABC123',
      lookup: 'Look up',
      lookingUp: 'Checking…',
      foundFor: 'For {name}',
      pointsHeld: '{points} pts held',
      discountValue: 'Face value {value}',
      alreadyOnOrder: 'Already applied to an order at checkout — do not discount the bill again by hand.',
      voucherExpires: 'Expires {date}',
      commitCta: 'Apply voucher',
      committing: 'Applying…',
      cancelCta: 'Cancel',
      successTitle: 'Voucher applied',
      successDescription: 'Points are now committed. The customer has been credited.',
      errorNotFound: 'No voucher found for that code.',
      errorAlreadyUsed: 'This voucher has already been used.',
      errorExpired: 'This voucher has expired.',
      errorGeneric: 'Could not apply the voucher. Please try again.',
    },
  },

  livechat: {
    launcher: {
      contactUs: 'Contact us',
      needHelp: 'Need help?',
    },
    picker: {
      title: 'How would you like to contact us?',
      whatsapp: 'WhatsApp',
      whatsappSub: 'Open in WhatsApp',
      line: 'LINE',
      lineSub: 'Open in LINE',
      webChat: 'Chat here',
      webChatSubAnonymous: 'Reply right in this page',
      webChatSubSignedIn: 'Web Chat (signed in)',
    },
    widget: {
      ariaLabel: 'Live chat',
      closeAria: 'Close chat',
      loading: 'Loading…',
      loadError: 'Could not load chat. Please refresh and try again.',
      poweredBy: 'Powered by NovixOne',
    },
    signin: {
      continueGoogle: 'Continue with Google',
      continueApple: 'Continue with Apple',
      continueEmail: 'Continue with email',
      emailHint: "We'll email you a 6-digit code.",
      emailPlaceholder: 'you@example.com',
      sendCode: 'Send code',
      sending: 'Sending…',
      back: '← Back',
      codeSentTo: 'Code sent to {contact}. Check your inbox.',
      otpPlaceholder: '123456',
      verify: 'Verify',
      verifying: 'Verifying…',
      errorInvalidEmail: 'Please enter a valid email',
      errorSendFailed: 'Could not send code. Please try again.',
      errorWrongCode: 'Wrong code. Please try again.',
      errorCodeNotSixDigits: 'Code must be 6 digits.',
      errorTooManyAttempts: 'Too many attempts. Send a new code.',
      errorCodeExpired: 'Code expired. Please send a new one.',
      errorVerifyFailed: 'Verification failed.',
    },
    conversation: {
      inputPlaceholder: 'Type a message…',
      sendButton: 'Send',
      sendingButton: '…',
      sendFailedSuffix: ' (failed to send)',
      defaultGreeting: 'Hi! I can help with bookings, products, and orders. Choose a sign-in method to start chatting.',
    },
  },
}

export default dict
